package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/kmm/basic")
@Composable
fun BasicKMMPage() {
    BlogLayout(
        PageProperties(
            "Basic",
            ""
        ),
        BlogProperties("Kotlin Multiplatform", "Basic")
    ) {
        Column(
            modifier = Modifier.fillMaxSize().padding(leftRight = 0.1.em),
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "What to choose: KMP or native development?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Multiplatform is great for reducing development time since you don’t need a separate developer for Android and another for iOS; one person can write shared logic in Kotlin that will work natively on both platforms. It also reduces development costs since updates and bug fixes need to be made only once and there is no duplication of work.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Native development generally incurs higher costs because it requires separate codebases for Android and iOS. If your application has very specific requirements for platform-specific features and needs to be as precise as possible (such as advanced camera functionalities, certain sensors, etc.), then native development is a good choice.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Quo Vadis Navigation Library",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Quo Vadis is a comprehensive, type-safe navigation library for Kotlin Multiplatform and Compose Multiplatform. It provides a clean, intuitive API for managing navigation across Android, iOS, Desktop, and Web platforms with zero string-based routing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose Multiplatform Latest Version",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    Beta: 1.11.0-beta01
                    Stable: 1.10.3
                    """.trimIndent()
            )

            SpanText(
                text = "Kotlin multiplatform Testing Library",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    xemantic-kotlin-test
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModelProvider & viewModelFactory",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/XDdevv/OctoberMiniChallengesKMP.git
            KotlinCode(
                code = """
                    //Support Compose, KMM
                    
                    import androidx.compose.runtime.Composable
                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.ViewModelProvider
                    import androidx.lifecycle.viewmodel.CreationExtras
                    import androidx.lifecycle.viewmodel.compose.viewModel
                    import kotlin.reflect.KClass

                    @Composable
                    inline fun <reified VM : ViewModel> composeViewModel(
                        crossinline creator: () -> VM
                    ): VM {
                        val factory = object : ViewModelProvider.Factory {
                            override fun <T : ViewModel> create(modelClass: KClass<T>, extras: CreationExtras): T {
                                @Suppress("UNCHECKED_CAST")
                                return creator() as T
                            }
                        }
                        return viewModel(factory = factory)
                    }
                    
                     HaunterThemeRoot(viewModel = composeViewModel { HauntedThemeViewModel() })
                    """.trimIndent()
            )

            //https://github.com/XDdevv/OctoberMiniChallengesKMP.git
            KotlinCode(
                code = """
                    //1
                    class FoodViewModel : ViewModel() { 
                        companion object {
                            val foodViewModelFactory = viewModelFactory {
                                initializer {
                                    FoodViewModel()
                                }
                            }
                        }
                    }
                    
                    val foodViewModel: FoodViewModel = viewModel(factory = foodViewModelFactory)
                    
                    //2 Multiple ViewModels in the same factory
                    //androidMain
                    viewModel = viewModel(factory = viewModelFactory {
                        addInitializer(MissedMessagesViewModel::class, initializer = {
                            MissedMessagesViewModel(AndroidMissedMessagedRepository())
                        })
                    })
                    
                    //iosMain
                    fun MainViewController() = ComposeUIViewController {
                        App(viewModel = viewModel(factory = viewModelFactory {
                            addInitializer(StillConnectedViewModel::class, initializer = {
                                StillConnectedViewModel(IOSConnectivityManager())
                            })
                        }))
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "KDoctor",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KDoctor is a command-line tool that helps to set up the environment for Kotlin multimodule app development. KDoctor ensures that all required components are properly installed and ready for use. If something is missed or not configured, KDoctor highlights the problem and suggests how to fix the problem.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    brew install kdoctor
                    """.trimIndent()
            )

            //https://github.com/mahdizareeii/Mini-Uber.git
            SpanText(
                text = "KotlinUnit",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s like Void in Swift - But since the Kotlin/Native compiler can’t directly map Kotlin’s Unit to Swift’s Void, it creates a wrapper type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You must return it explicitly - If you’re writing a closure in Swift that Kotlin expects as (SomeType) -> Unit, you need to return KotlinUnit():",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Set up ViewModel for KMP",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //libs.versions.toml
                    [versions]
                    androidx-viewmodel = 2.9.3

                    [libraries]
                    androidx-lifecycle-viewmodel = { module = "androidx.lifecycle:lifecycle-viewmodel", version.ref = "androidx-viewmodel" }
                    androidx-runtime-compose = { module = "org.jetbrains.androidx.lifecycle:lifecycle-runtime-compose", version.ref = "androidx-viewmodel" }
 
                    //build.gradle.kts
                    //You need the "api" dependency declaration here if you want better access to the classes from Swift code.
                    commonMain.dependencies {
                      api(libs.androidx.lifecycle.viewmodel)
                      api(libs.androidx.runtime.compose)
                    }
                    
                    listOf(
                      iosX64(),
                      iosArm64(),
                      iosSimulatorArm64(),
                    ).forEach {
                      it.binaries.framework {
                        // Add this line to all the targets you want to export this dependency
                        export(libs.androidx.lifecycle.viewmodel)
                        baseName = "shared"
                      }
                    }
                    
                    //JVM Desktop
                    // Optional if you use JVM Desktop
                    desktopMain.dependencies {
                        implementation("org.jetbrains.kotlinx:kotlinx-coroutines-swing:[KotlinX Coroutines version]")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Installation SKIE",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //build.gradle.kts
                    plugins {
                        id("co.touchlab.skie") version "0.10.6"
                    }
                    
                    //settings.gradle.kts
                    pluginManagement {
                        repositories {
                            google()
                            mavenCentral()
                            gradlePluginPortal()
                        }
                    }

                    dependencyResolutionManagement {
                        repositories {
                            google()
                            mavenCentral()
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/GuilhE/Expressus.git
            KotlinCode(
                code = """
                    plugins {
                        id("buildlogic.plugins.kmp.library.android")
                        id("kotlin-parcelize")
                        kotlin("native.cocoapods")
                        alias(libs.plugins.google.ksp)
                        alias(libs.plugins.kotlinx.serialization)
                    }

                    android {
                        namespace = ""
                    }

                    kotlin {
                        jvm()
                        androidTarget()
                        listOf(iosArm64(), iosSimulatorArm64(), iosX64()).forEach { target ->
                            target.compilerOptions { freeCompilerArgs.add("-Xexport-kdoc") }
                            //freeCompilerArgs.add("-Xbinary=bundleId=bundleName") //BundleName
                        }

                        cocoapods {
                            summary = ""
                            homepage = ""
                            ios.deploymentTarget = "17.0"
                            version = "1.0"
                            podfile = project.file("../iosApp/Podfile")
                            framework {
                                baseName = "ExpressusShared"
                            }
                        }

                        sourceSets {
                            commonMain.dependencies {
                                implementation(libs.kotlinx.coroutines.core)
                                implementation(libs.kotlinx.serialization)
                                implementation(libs.kmp.settings)
                                api(libs.androidx.lifecycle.viewmodel)
                                api(libs.kmp.kermit)
                                api(libs.kmp.orbitMvi.core)
                                api(libs.kmp.koin.core)
                            }
                            androidMain.dependencies { implementation(libs.kmp.koin.android) }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Cleanup",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //cleanup.sh

                    #!/bin/sh
                    rm -rf .idea
                    ./gradlew clean
                    rm -rf .gradle
                    rm -rf build
                    rm -rf */build
                    rm -rf iosApp/Pods
                    rm -rf iosApp/iosApp.xcworkspace
                    rm -rf iosApp/iosApp.xcodeproj/project.xcworkspace
                    rm -rf iosApp/iosApp.xcodeproj/xcuserdata
                    
                    #Clean everything
                    pod deintegrate
                    rm -rf Pods/ Podfile.lock
                    rm -rf ~/Library/Developer/Xcode/DerivedData
                    ./gradlew clean

                    #Reinstall
                    pod install
                    
                    //Xcode Integration Gotchas
                    ./gradlew clean publishToMavenLocal
                    ./gradlew spmDevBuild
                    """.trimIndent()
            )

            SpanText(
                text = "iOSTarget block",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    ./gradlew podspec
                    
                    //org.gradle.internal.UncheckedException: org.jetbrains.kotlin.konan.MissingXcodeException: An error occurred during an xcrun execution. Make sure that Xcode and its command line tools are properly installed. Failed command: /usr/bin/xcrun xcodebuild -version
                    sudo rm -rf /Library/Developer/CommandLineTools
                    xcode-select --install
                    """.trimIndent()
            )

            SpanText(
                text = "Remove old Xcode simulators",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    Xcode → Settings → Components → Other Installed Platforms
                    """.trimIndent()
            )

            SpanText(
                text = "xtool",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A cross-platform Xcode replacement that lets you build and deploy iOS apps using SwiftPM on Linux, Windows (WSL), and macOS.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    brew install xtool-org/tap/xtool
                    """.trimIndent()
            )

            SpanText(
                text = "pod command",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    pod install – Install pods listed in Podfile.
                    pod update – Update all pods to latest version.
                    pod update Alamofire – Update specific pod.
                    pod deintegrate – Remove CocoaPods from Xcode project.
                    pod repo update – Update local podspec repo.
                    
                    ./gradlew :shared:podinstall - First time only) Run the following command to generate the Xcode project file.
                    """.trimIndent()
            )

            SpanText(
                text = "iosApp.xcodeproj not found",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    # Build shared framework
                    ./gradlew :shared:linkPodDebugFrameworkIosSimulatorArm64

                    # Generate Xcode project (required after cloning or project.yml changes)
                    cd iosApp
                    xcodegen generate

                    # Install CocoaPods dependencies
                    pod install

                    # Open workspace in Xcode
                    open iosApp.xcworkspace

                    # Or build from command line
                    xcodebuild -workspace iosApp.xcworkspace -scheme iosApp \
                    -destination 'platform=iOS Simulator,name=iPhone 16' \
                    -configuration Debug build
                """.trimIndent()
            )

            SpanText(
                text = "pod delete",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    rm -rf iosApp/Pods  
                    rm -rf iosApp/Podfile.lock
                    rm -rf ~/.cocoapods/repos
                    pod repo update
                    ./gradlew podInstall

                    cd iosApp && pod install --repo-update
                    """.trimIndent()
            )

            SpanText(
                text = "Separate module for resources",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    plugins {    
                        kotlin("multiplatform")    
                        id("com.android.library") 
                        id("dev.icerock.mobile.multiplatform-resources")
                    }
                    
                    kotlin {
                        android()

                        ios {
                            binaries {
                                framework {
                                    baseName = "shared"
                                    export(project(":sharedResources"))
                                    export(Deps.Resources.core)
                                }
                            }
                        }
                        
                        sourceSets {
                            commonMain {
                                dependencies {
                                    api(project(":sharedResources"))
                                    api(Deps.Resources.core)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Gradle 9 + KMP Android Launcher Icon Crash",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        packaging { // THIS IS THE EXACT THING THAT WE NEED
                            resources {
                                excludes += "/META-INF/{AL2.0,LGPL2.1}"
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "Kotlin Multiplatform: shared resources across modules",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    compose.resources {
                        publicResClass = true
                        packagefResClass = "package name"
                        generateResClass = auto
                    }
                    """.trimIndent()
            )

            //https://github.com/inlineTushar/TopCoins-KMM.git
            KotlinCode(
                code = """
                    alias(libs.plugins.touchlab.skie)
                    
                    kotlin {
                        // Configure iOS framework export
                        targets.withType<org.jetbrains.kotlin.gradle.plugin.mpp.KotlinNativeTarget> {
                            binaries.withType<org.jetbrains.kotlin.gradle.plugin.mpp.Framework> {
                                // Export all dependencies to make them available to iOS
                                export(project(":common:core"))
                                export(project(":common:ui"))
                                export(project(":common:data"))
                                export(project(":common:domain"))
                            }
                        }
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "compose.resources",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //3. https://github.com/thejohnsondev/PassGuard-Multiplatform
            KotlinCode(
                code = """
                    //1
                    commonMain.dependencies {
                        implementation(compose.components.resources)
                    }
                    
                    android {
                        androidResources {
                            generateLocaleConfig = true
                        }
                    }
                    
                    //2
                    compose.resources {
                        publicResClass = true
                        generateResClass = auto
                    }
                    
                    //3
                    compose.resources {
                        publicResClass = true
                        packageOfResClass = "package_name"
                        generateResClass = always
                    }
                    
                    android {
                        sourceSets["main"].resources.srcDirs("src/commonMain/resources")
                        
                        lint {
                            disable.add("NullSafeMutableLiveData")
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Ma7moud3ly/quran-multiplatform.git
            KotlinCode(
                code = """
                     //koin annotation using font
                    
                     /**
                      * Manages available fonts within the application.
                      *
                      * Provides a centralized list of `AppFont` objects, each with an ID, name,
                      * and font resource. Includes a default font and methods to retrieve all fonts
                      * or a specific font by ID.
                      */
                     @Single
                     class FontsManager() {
                         companion object {
                             private val fonts = listOf(
                                 AppFont(
                                     id = "",
                                     name = "",
                                     fontType = Res.font.
                                 ), 
                                 AppFont(
                                     id = "",
                                     name = "",
                                     fontType = Res.font.
                                 ),
                                 AppFont(
                                     id = "",
                                     name = " ",
                                     fontType = Res.font.
                                 ),
                                 AppFont(
                                     id = "",
                                     name = "",
                                     fontType = Res.font.,
                                     letterSpacing = 2.sp,
                                     lineHeight = 2.5.em
                                 )
                             )

                             const val DEFAULT_FONT = ""
                             fun first() = fonts.firstOrNull { it.id == DEFAULT_FONT } ?: fonts.first()
                         }

                         fun getFonts(): List<AppFont> = fonts
                         fun getFont(id: String) = fonts.firstOrNull { it.id == id } ?: fonts.first()
                     }
                     
                     //access
                     FontsManager().getFonts()
                """.trimIndent()
            )

            SpanText(
                text = "Bundles",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using bundles from our version catalog groups related components (like ktor or navigation3) into a single line, keeping the build file clean.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "KSP Plugin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KSP analyzes annotations during compilation to generate Koin modules. This catches errors early and improves runtime performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "KSP Configuration",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using the ksp configuration ensures the compiler only processes changed symbols, leading to faster iterative builds.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "sourceSets",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In a Kotlin Multiplatform project, you’re writing shared code (commonMain), and platform-specific code (androidMain, iosMain, etc.).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each of these blocks of code lives in a source set, and Gradle needs to know:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "what each source set is called,",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "what it depends on,",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "and where to find its files.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Kotlin DSL, the sourceSets block is where you declare and configure all of this. KSP generates files in the build folder. We must explicitly include these paths as source directories so the IDE recognizes the generated Koin classes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Convert (bytesToHexString, hexStringToByteArray)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    fun bytesToHexString(bytes: ByteArray?): String? {
                        if (bytes == null) return null
                        return bytes.joinToString("") { (it.toInt() and 0xFF).toString(16).padStart(2, '0') }
                    }

                    fun hexStringToByteArray(hex: String?): ByteArray? {
                        if (hex == null || hex.lowercase() == "null") return null
                        if (hex.isEmpty()) return byteArrayOf()
                        require(hex.length % 2 == 0) { "Hex string must have an even length or be empty. Received: '$hex'" }
                        return hex.chunked(2)
                            .map { it.toInt(16).toByte() }
                            .toByteArray()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Module",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    commonMain.dependencies {
                        api(project(":core:model"))
                        
                        implementation(project(":core:model"))
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "build.gradle.kts",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/nyuksoonvong3432/kmp-todoist-app
            KotlinCode(
                code = """
                        //We export dependencies so Swift can see "Store", "StateFlow", etc.
                        val xcf = XCFramework(xcFrameworkName = "TodoistCore")
                        
                        listOf(
                            iosX64(),
                            iosArm64(),
                            iosSimulatorArm64()
                        ).forEach {
                            it.binaries.framework {
                                baseName = "TodoistCore"
                               
                                isStatic = true // Static frameworks are often easier for SPM distribution
                                
                                export (libs. decompose)
                                export(libs.mvikotlin.main)
                                
                                linkerOpts.add("-lsqlite3")
                                xcf.add(this)
                            }
                        }

                        //Platform specific code
                        kotlin {
                            cocoapods {
                                version = "1.0.0"
                                summary = "KMM Shared Module"
                                homepage = "https://example.com"
                                ios.deploymentTarget = "13.0"
                                framework {
                                    baseName = "shared"
                                    isStatic = true
                                }
                            }

                            listOf(
                                iosX64(),
                                iosArm64(),
                                iosSimulatorArm64()
                            ).forEach {
                                it.binaries.framework {
                                    baseName = "shared"
                                    linkerOpts("-lsqlite3")
                                    isStatic = true
                                }
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "AppDelegate",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When integrating third-party services that require delegate methods like push notifications, deep linking, or background tasks. To do that, we can define an AppDelegate class and connect it to our SwiftUI app using UIApplicationDelegateAdaptor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/arrazyfathan/tudu-mobile.git
            KotlinCode(
                code = """
                    //AppDelegateAdapter.kt
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.UIKit.UIApplication
                    import kotlin.experimental.ExperimentalObjCName

                    @OptIn(ExperimentalObjCName::class)
                    @ObjCName(swiftName = "AppDelegateAdapter")
                    class AppDelegateAdapter {
                        @OptIn(ExperimentalForeignApi::class)
                        fun application(
                            application: UIApplication,
                            didFinishLaunchingWithOptions: Map<Any?, *>?,
                        ): Boolean {
                            initNapier()
                            return true
                        }
                    }
                    
                    //initNapier.kt
                    import io.github.aakira.napier.DebugAntilog
                    import io.github.aakira.napier.Napier

                    fun initNapier() {
                        Napier.base(DebugAntilog())
                    }
                    
                    //iosApp
                    @main
                    struct iOSApp: App {
                        //Connecting AppDelegate with the app using UIApplicationDelegateAdaptor
                        @UIApplicationDelegateAdaptor(AppDelegate.self) var appDelegate
                        
                        init() {
                            //FirebaseApp.configure()
                        }
                        
                        var body: some Scene {
                            WindowGroup {
                                ContentView()
                            }
                        }
                    }
                    
                    import Foundation
                    import SwiftUI
                    import ComposeApp

                    class AppDelegate: NSObject, UIApplicationDelegate {
                        private let appDelegateAdapter = AppDelegateAdapter()
                        
                        func application(_ application: UIApplication, didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey : Any]? = nil) -> Bool {
                                return appDelegateAdapter.application(application: application, didFinishLaunchingWithOptions: launchOptions)
                            }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "KMP ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    sourceSets {
                        commonMain {
                            dependencies {
                                implementation("androidx.lifecycle:lifecycle-viewmodel:2.8.0-alpha03")
                            }
                        }
                    }

                    implementation("androidx.activity:activity-compose") {
                        exclude(group = "androidx.lifecycle", module = "lifecycle-viewmodel-ktx") // Here we are exlcuding the ViewModel dependency
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Compose Multiplatform 1.7.0",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Multiplatform 1.7.0 you can also try out the experimental API for more precise control of the interop UI. New constructors for UIKitView or a UIViewController accept a UIKitInteropProperties object as an argument. This object allows you to set: ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The interactionMode parameter for the given interop view, which allows you to select a touch processing strategy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The isNativeAccessibilityEnabled option, which changes the accessibility behavior for the interop view.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "buildConfig for multiplatform",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/walter-retzer/HalfMouthAppProject
            KotlinCode(
                code = $$"""
                        /***   Read from file name secret.properties  ***/
                        fun readPropertiesFromFile(fileName: String): Map<String, String> {
                            val parts = fileName.split('.')
                            val localPropertyFileName = if (parts.size >= 2) {
                                val nameWithoutExtension = parts.dropLast(1).joinToString(".")
                                val extension = parts.last()
                                "$nameWithoutExtension.local.$extension"
                            } else {
                                fileName
                            }
                            val isLocalFileExists= File(project.rootDir,localPropertyFileName).exists()
                            val fileContent = try {
                                File(project.rootDir,if (isLocalFileExists) localPropertyFileName else fileName).readText()
                            } catch (e: Exception) {
                                e.printStackTrace()
                                return emptyMap()
                            }

                            val properties = mutableMapOf<String, String>()

                            fileContent.lines().forEach { line ->
                                val keyValuePair = line.split('=')
                                if (keyValuePair.size == 2) {
                                    properties[keyValuePair[0].trim()] = keyValuePair[1]
                                }
                            }
                            return properties
                        }

                        /** If System.env value exists it will be returned value which can be useful for CI/CD pipeline **/
                        fun Map<String, String>.getPropertyValue(key: String): String? {
                            val envValue = System.getenv(key)
                            if (envValue.isNullOrEmpty().not()) return envValue
                            return this.getOrDefault(key, null)
                        }

                        sourceSets {
                            // convert the task to a file-provider for a secret keys file
                            commonMain{
                                kotlin.srcDir(buildConfigGenerator.map { it.destinationDir })
                            }
                        }

                        //Generating BuildConfig for multiplatform
                        val buildConfigGenerator by tasks.registering(Sync::class) {
                            val packageName = "secrets"
                            val secretProperties = readPropertiesFromFile("secrets.properties")
                            from(
                                resources.text.fromString(
                                    ""$${'"'}
                                    |package  $packageName
                                    |
                                    |object BuildConfig {
                                    |  const val API_KEY = "${secretProperties.getPropertyValue("API_KEY")}"
                                    |  const val CHANNEL_ID = "${secretProperties.getPropertyValue("CHANNEL_ID")}"
                                    |  const val RESULTS = "${secretProperties.getPropertyValue("RESULTS")}"
                                    |  const val SIMPLE_RESULTS = "${
                    secretProperties.getPropertyValue(
                        "SIMPLE_RESULTS"
                    )
                }"
                                    |}
                                    |
                                    ""$${'"'}.trimMargin()
                                )
                            )
                            {
                                rename { "BuildConfig.kt" } // set the file name
                                into(packageName) // change the directory to match the package
                            }
                            into(layout.buildDirectory.dir("generated-secret-keys/kotlin/"))
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "Compose Multiplatform (CMP) - (Share everything - logic + UI)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "According to JetBrains’ website, Compose Multiplatform is a declarative framework for sharing UIs across and logic multiple platforms, based on Kotlin and Jetpack Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In practice, a CMP project often looks like this:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "shared Kotlin module with business logic",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "shared Compose UI module",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Android/iOS host apps responsible for platform entry points and integrations",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Kotlin Multiplatform (KMP) - (Share business logic, keep native UI)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Multiplatform (or KMP for short) is an initiative from the Czech company JetBrains, which designs tools for development teams and is notably at the origin of the reference language for native Android applications, Kotlin.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Share logic and keep the Ul native Use Kotlin Multiplatform when you start a new project, and implement data handling and business logic just once. Keep the Ul native to meet the most stringent requirements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A so-called “cross-platform” technology, KMP aims to share Kotlin code on various platforms such as Android, iOS, or even the different operating systems of our computers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMP began in 2017 and will reach its first stable version in 2023!",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A real production KMP module often ends up holding:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "API clients and request logic",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Database schemas and queries",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repository implementations",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Domain use cases and validation rules",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Shared state containers or presentation logic",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Why Choose KMM?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Efficiency: Write once, deploy everywhere. KMM allows you to reuse business logic, reducing the need for duplicate work.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Consistency: Shared code means fewer discrepancies between your Android and iOS apps.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Native Performance: KMM ensures your shared code runs with native performance on both platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Community and Support: Backed by JetBrains and a growing community, KMM is continuously evolving with robust support and resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why CocoaPods is Needed in Kotlin Multiplatform (KMP)?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CocoaPods is a dependency manager for iOS (like Gradle for Android).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMP uses CocoaPods to link shared Kotlin code into iOS projects easily. For iOS, you use CocoaPods to include third-party dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //Swift Package Manager Setup (XCFramework)
            //Export the Kotlin Multiplatform library as an XCFramework so it can be consumed by Swift and Xcode.
            //Kotlin Multiplatform does not publish directly to Swift Package Manager — instead, it exports native frameworks that SPM can download.

            /*
            compilerOptions {
                apiVersion.set(org.jetbrains.kotlin.gradle.dsl.KotlinVersion.KOTLIN_2_1)
                languageVersion.set(org.jetbrains.kotlin.gradle.dsl.KotlinVersion.KOTLIN_2_1)
            }*/

            //swiftPMDependencies
            //https://medium.com/@adman.shadman/no-cocoapods-needed-using-swift-packages-directly-in-kotlin-multiplatform-75be3d5c036e
            //https://github.com/frankois944/spm4Kmp/blob/dependabot/gradle/kotlin-2.3.0/example/build.gradle.kts
            //https://github.com/frankois944/FirebaseKmpDemo.git
            KotlinCode(
                code = """
                    //Goodbye to CocoaPods and welcome Swift Package Manager to Kotlin Multiplatform
                    //Gradle plugin for Kotlin Multiplatform projects that generates an XCFramework for your native Apple targets and creates a matching Package.swift file to distribute it as a binary target.
                    
                    // ./gradlew createSwiftPackage //I will use the Multiplatform swift package Gradle plugin. After setting this up, we can run the command
                    
                    kotlin.mpp.enableCInteropCommonization=true
                    kotlin.mpp.enableCInteropCommonization.nowarn=true
                    kotlin.native.cacheKind.iosSimulatorArm64=none
                   
                    spmforkmp.enableTracing=true
                    
                    swiftPMDependencies { }
                    """.trimIndent()
            )

            //https://github.com/moses26222/cat-paywalls-kmp
            //https://github.com/mitulbharatchandra/WorldWideMoto (Firebase)
            //https://github.com/thementalgoose/kmp-flashback.git
            KotlinCode(
                code = """
                    import org.jetbrains.kotlin.gradle.ExperimentalKotlinGradlePluginApi
                    import org.jetbrains.kotlin.gradle.dsl.JvmTarget

                    plugins {
                      kotlin("native.cocoapods")
                    }

                    kotlin {
                      androidTarget {
                        @OptIn(ExperimentalKotlinGradlePluginApi::class)
                        compilerOptions {
                          jvmTarget.set(JvmTarget.JVM_11)
                        }
                      }

                      listOf(
                        iosX64(),
                        iosArm64(),
                        iosSimulatorArm64(),
                      ).forEach { iosTarget ->
                        iosTarget.binaries.framework {
                          baseName = "ComposeApp"
                          isStatic = true
                        }
                      }

                      cocoapods {
                        summary = "Cat Paywalls KMP App"
                        homepage = "https://github.com/revenuecat/cat-paywalls-kmp"
                        version = "1.0"
                        ios.deploymentTarget = "15.0"
                        podfile = project.file("../iosApp/Podfile")
                        framework {
                          baseName = "ComposeApp"
                          isStatic = true
                        }
                        pod("RevenueCat") {
                          version = "~> 5.21"
                          extraOpts += listOf("-compiler-option", "-fmodules")
                        }
                        pod("RevenueCatUI") {
                          version = "~> 5.21"
                          extraOpts += listOf("-compiler-option", "-fmodules")
                        }
                      }

                      sourceSets {
                        commonMain.dependencies {
                        }

                        androidMain.dependencies {
                        }

                        iosMain.dependencies {
                          implementation(libs.ktor.client.darwin)
                        }
                      }
                    }

                    android {
                      namespace = ""
                      compileSdk = libs.versions.android.compileSdk.get().toInt()

                      defaultConfig {
                        applicationId = ""
                        minSdk = libs.versions.android.minSdk.get().toInt()
                        targetSdk = libs.versions.android.targetSdk.get().toInt()
                        versionCode = 1
                        versionName = "1.0"
                      }

                      buildFeatures {
                        compose = true
                      }

                      compileOptions {
                        sourceCompatibility = JavaVersion.VERSION_11
                        targetCompatibility = JavaVersion.VERSION_11
                      }

                      packaging {
                        resources {
                          excludes += "/META-INF/{AL2.0,LGPL2.1}"
                        }
                      }
                    }
                    """.trimIndent()
            )

            //https://github.com/yankeppey/kmp-maps-compose
            //https://spmforkmp.eu/migration/migrateFromCocoapods
            //https://frankois944.github.io/spm4Kmp/
            KotlinCode(
                code = """
                    import io.github.frankois944.spmForKmp.swiftPackageConfig
                    import org.jetbrains.kotlin.gradle.dsl.JvmTarget

                    plugins {
                        alias(libs.plugins.kotlinMultiplatform)
                        alias(libs.plugins.androidKmpLibrary)
                        alias(libs.plugins.composeMultiplatform)
                        alias(libs.plugins.composeCompiler)
                        alias(libs.plugins.spmForKmp)
                        alias(libs.plugins.vanniktechMavenPublish)
                    }

                    group = "eu.buney.maps"
                    version = "0.1.0"

                    kotlin {
                        // Suppress warnings about expect/actual classes being in Beta
                        compilerOptions {
                            freeCompilerArgs.add("-Xexpect-actual-classes")
                        }

                        androidLibrary {
                            namespace = "eu.buney.maps"
                            compileSdk = 36
                            minSdk = 24

                            compilerOptions {
                                jvmTarget.set(JvmTarget.JVM_11)
                            }
                        }

                        listOf(
                            iosX64(),
                            iosArm64(),
                            iosSimulatorArm64()
                        ).forEach { iosTarget ->
                            iosTarget.binaries.framework {
                                baseName = "MapsComposeMp"
                                isStatic = true
                            }

                            iosTarget.swiftPackageConfig(cinteropName = "GoogleMapsBridge") {
                                minIos = "17.0"
                                dependency {
                                    remotePackageVersion(
                                        url = uri("https://github.com/googlemaps/ios-maps-sdk"),
                                        products = {
                                            add("GoogleMaps", exportToKotlin = true)
                                        },
                                        version = "10.6.0"
                                    )
                                }
                                // Export GoogleMaps package so iOS app can use it directly
                                exportedPackageSettings {
                                    includeProduct = listOf("GoogleMaps")
                                }
                            }
                        }

                        sourceSets {
                            commonMain.dependencies {
                            }

                            androidMain.dependencies {
                            }

                            iosMain.dependencies {
                                // Google Maps iOS SDK via SPM
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "How Kotlin Multiplatform Apps Run on Android & iOS?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "Android side",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Gradle target: androidTarget() (implicit in the Android plugin).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Kotlin/JVM compiler turns your code into .class files, then D8/R8 repacks them into Dalvik byte-code (.dex).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = ".dex is executed by Android Runtime (ART) ‑ a JVM-like engine baked into every device. ART may JIT or AOT-compile this byte-code to machine code during install or at runtime.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "With Compose Multiplatform, your UI layer is pure Jetpack Compose, so it renders through the native Android graphics stack — no extra bridges.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                SpanText(
                    text = "iOS side",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Gradle targets: iosArm64() (device), iosX64() (Intel simulator), iosSimulatorArm64() (Apple-Silicon simulator).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Kotlin/Native feeds LLVM and produces a Mach-O binary. No JVM, no .dex, no bridge.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The binary runs directly on iOS. Swift/Obj-C can call into it via generated headers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use UIKit/SwiftUI if you want, or plug in Compose Multiplatform — it draws with Skia while all business logic remains 100 % native.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Bug Fixed",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "org.jetbrains.kotlin.konan.MissingXcodeException: An error occurred during an xcrun execution. Make sure that Xcode and its command line tools are properly installed. Failed command: /usr/bin/xcrun xcodebuild -version",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    sudo xcode-select -r
                    """.trimIndent()
            )

            SpanText(
                text = "Do we say KMP or KMM?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We say KMP for “Kotlin Multiplatform” and KMM for Kotlin multimodule. But in reality, it’s the same thing today.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If KMM was initially launched in 2020 to designate a subset of KMP intended for mobile uses only (this was from the start the preferred target), JetBrains clarified things and decreed that only the more global KMP terminology, had to be used.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Kotlin code be invoked from within a Java",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To call Kotlin code from inside a Java service class, you can use Kotlin’s interoperability features.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    class MyKotlinClass {
                        fun getData(): Flow<String> {
                            return flow {
                                for (i in 1..3) {
                                    delay(1000)
                                    emit(i)
                                }
                            }
                        }
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    public class MyJavaService {

                        public void fetchData() {
                            MyKotlinClass myKotlinClass = new MyKotlinClass();

                            Flow<String> flow = myKotlinClass.getData();
                            CoroutineScope scope = new CoroutineScope(Dispatchers.Default);

                            scope.launch(() -> {
                                flow.collect(System.out::println);
                            });
                        }
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "we create an instance of MyKotlinClass and call its getData() function to obtain a Flow<String>. We then use CoroutineScope and launch to collect items from the flow and print them. Make sure to handle the coroutine scope properly to avoid memory leaks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KotlinInterop is a feature of Kotlin that allows seamless interoperability between Kotlin and Java code. It ensures that Kotlin code can be called from Java and vice versa without any major hurdles.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, when you define a Kotlin function or class and want to use it in Java, the Kotlin compiler generates a corresponding Java-accessible class or method, making it possible to use Kotlin constructs in Java code. This is particularly useful when you’re working in a mixed-language environment or transitioning from Java to Kotlin gradually, as it allows you to leverage existing Java code while taking advantage of Kotlin’s features.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Can we manage differences in functionality between platforms despite the common code?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Multiplatform provides annotations to specify different code for each platform if a part requires it. If we take the example of saving data, each OS has a system in place, NSUserDefaults on iOS and the DataStore on Android.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is possible in KMP to do a function once which, depending on the OS, will either use the NSUserDefaults or the DataStore.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This function can then be used everywhere, without being implemented outside the KMP library, in the application code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/GuilhE/Expressus/blob/main/shared-ui-compose/shared_ui_compose.podspec
            SpanText(
                text = "Build Phases",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.BUILD_PHASE,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Build Phases"
            )

            SpanText(
                text = "Code sharing with Kotlin Multiplatform",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Multiplatform, we have the flexibility to choose to what extent we want to share our code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Below, we can see how this framework provides us with different approaches, allowing us to decide whether to share only a small portion of our code or even share the entire code, including the UI, using Compose Multiplatform.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                WebImage(
                    src = Resources.Images.KMM_1,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "KMM"
                )

                SpanText(
                    text = "Compose Multiplatform",
                    modifier = Modifier.fontSize(FontSize.Large)
                        .fontWeight(FontWeight.Bold)
                )

                WebImage(
                    src = Resources.Images.CODE_SHARING_PLATFORM,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Code sharing platform"
                )

                WebImage(
                    src = Resources.Images.COMPOSE_MULTIPLATFORM_SHARE_UI,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Share UI (Presentation Layer)"
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For cases where you want to share only the UI components, Kotlin Multiplatform can be applied using Compose Multiplatform.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This allows you to create and reuse UI code across platforms while keeping the business logic and data handling platform specific.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                WebImage(
                    src = Resources.Images.COMPOSE_MULTIPLATFORM,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Compose multiplatform"
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "As you can see in the diagram, the general approach of Kotlin Multiplatform includes:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Code for iOS-specific APIs like Bluetooth, CoreData etc.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Shared code for business logic",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI on the iOS side. (You have a choice to use native iOS UI frameworks (UIKit or SwiftUI), or embed iOS code directly into Compose.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Once you’ve set up the base project, you will see three primary directories:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "androidApp",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The androidApp module is just an entry point for the Android application. androidApp and shared are modules because they are related to Android and built with build.gradle.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "iosApp",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The iosApp is the directory of the actual ios application, which you can open via Xcode. ",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "shared",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "This Kotlin module contains shared logic for both Android and iOS implementations, facilitating the use of the same codebase across both platforms.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Within the shared module, you’ll find three directories, each serving its own purpose: commonMain, androidMain, and iosMain.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "This a point of confusion - in fact, the code that is actually shared lies inside the commonMain directory.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The other two directories are for writing platform-specific Kotlin code that will either behave or look differently on Android or iOS.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "This is done by writing an expect fun inside of the commonMain code, and implementing it using actualFun in the corresponding platform directory.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "KMM and KMP",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMM (Kotlin multimodule) and KMP (Kotlin Multiplatform) are both Kotlin-based technologies that allow you to share code between different platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, they are used in slightly different contexts:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "KMM Internal working",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                WebImage(
                    src = Resources.Images.KMM_INTERNAL_WORKING,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "KMM internal working"
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Focus: KMM is specifically designed for sharing code between Android and iOS apps, allowing you to write common business logic, data processing, and networking code that can be used on both platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Usage: KMM is used for mobile app development, where you want to reduce duplication of code and ensure consistency across Android and iOS apps.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Tools: KMM provides tools and libraries specifically tailored for mobile development, such as integration with Android Studio and Xcode.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "KMM shared Services architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "androidApp (native)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Views implemented either with XML or Jetpack Compose.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Presentation logic holder of your choice. It can be a View Model or some other solution.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Contains Android-specific code. This is where you might implement expect/actual patterns for things like platform-specific databases or other APIs that have different implementations on Android.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "iosApp (native)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Views implemented either with UIKit or SwiftUI. The Swift/Objective-C code in your iOS app can call into the Kotlin code from this framework.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Presentation logic holder of your choice. It can be a View Model or some other solution.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Similar to the Android source set, but for iOS. Here you’d write the Swift/Objective-C interop code or implement expect/actual functions using iOS-specific APIs.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "shared (KMM)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Services handle a business logic. They expose public methods which return data or perform some operations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Services access different Repositories to get or save data.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repositories handle data logic. They expose CRUD operations which return or update data.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repositories access different Data Sources in order to fetch or persist data locally or remotely.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Common",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = " Contains all the code that is shared between platforms. This is where you write the majority of your business logic.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Share Kotlin code between multiple platforms",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Shared code is placed in a common module and is used by all platforms. Sometimes we need to access platform APIs and Kotlin Multiplatform allows you calling platform-specific code at any time through the expect/actual mechanism.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Platform-specific code is placed in platform-specific modules eg. ios, android and is used only by the specified platform.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Convert Compose Multiplatform iOS code to Kotlin native",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you use Compose Multiplatform on iOS, the Kotlin code for your UI is compiled to native code using Kotlin/Native. This native code is then used to create a UIKit-based UI that runs on the iOS platform. Compose Multiplatform for iOS provides the Kotlin APIs for building Compose UIs on iOS. This library bridges the gap between the Kotlin/Native code and the UIKit framework.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Multiplatform introduces a prototype for bidirectional interaction at the UI level. With the help of UIKitView, you can seamlessly integrate intricate platform-specific widgets — such as maps, web views, media players, and camera feeds — into your shared user interface. Conversely, employing ComposeUIViewController allows you to nest Compose Multiplatform screens within SwiftUI applications, facilitating a gradual integration of Compose Multiplatform into your iOS apps.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How Kotlin code convert to other platforms?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For Android Kotlin code convert to Java bytecode.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For iOS, Koltin code must be converted to the source code (native code executable on iOS) that goes inside the framework. That source code works on iOS.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Interoperability with SwiftUI and UIKit views",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Multiplatform on iOS allows two-way interoperability with native user interfaces: You can embed complex UI views like MapKit’s MKMapView in your Compose UI, or embed Compose screens in a SwiftUI application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Multiplatform is interoperable with the UIKit framework. You can embed Compose Multiplatform within a UIKit application as well as embed native UIKit components within Compose Multiplatform. UIKitView, you can embed complex platform-specific widgets like maps, web views, media players, and camera feeds within your shared user interface. In the other direction, via ComposeUIViewController, you can embed Compose Multiplatform screens in SwiftUI applications, helping you gradually adopt Compose Multiplatform in iOS applications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                SpanText(
                    text = "Compose Multiplatform (CMP)",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ComposeUlViewController: CMP function that returns a UlViewController.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UIKitViewController: Includes a UlViewController in a Compose view.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }

                SpanText(
                    text = "SwiftUl",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UlViewControllerRepresentable: SwiftUl view representing a UlKitViewController.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UlHostingController: UIKitViewController that manages a SwiftUl view.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }
            }
            SpanText(
                text = "How does Kotlin Multiplatform work?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " A Kotlin language feature + ecosystem that allows you to share code across multiple platforms (Android, iOS, Web, Desktop, JVM, Native).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Multiplatform functions through the use of specialised compilers that translate Kotlin code into the native code of the target platforms. The three primary compilers are:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                //https://blog.csdn.net/weixin_37042458/article/details/148681758
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin/JVM: Translates Kotlin into Java for use on JVM-based platforms. Kotlin/JVM is the original and most commonly used Kotlin compiler backend. It compiles Kotlin code into bytecode that can run on the Java Virtual Machine (JVM). Due to Kotlin's high interoperability with Java, Kotlin/JVM can directly utilize existing Java libraries and frameworks, allowing developers to easily integrate Kotlin with Java projects. Use cases include Android development and server development.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin/JS: Converts Kotlin code into JavaScript for web applications, allowing it to run in browsers or Node.js environments. Kotlin/JS enables front-end developers to write web applications using Kotlin while fully leveraging existing JavaScript libraries and frameworks. Its use case is web front-end development.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin/Native: Compiles Kotlin into C or Objective-C for use on platforms like iOS and other Apple operating systems. Kotlin/Native compiles Kotlin into native binary code, which can run directly in environments that do not rely on a JVM or JavaScript engine. Kotlin/Native supports multiple platforms, including iOS, Windows, macOS, Linux, embedded devices, and even Android and HarmonyOS, enabling cross-platform development using KN (requiring the writing of JNI or NAPI bridging code). KMP combines the advantages of Kotlin/JVM, Kotlin/JS, and Kotlin/Native, allowing developers to develop for different platforms within a unified codebase, achieving cross-platform solutions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These compilers ensure that the code written in Kotlin is transformed into the appropriate native code before deployment. Consequently, the final product does not include any Kotlin code but consists entirely of the native code, akin to applications developed without KMP. This process enhances performance and integration while maintaining the benefits of Kotlin’s expressive syntax and robust features.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How KMP works? (KMP Implementation Principle)",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.KMP_WORK,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "KMP Work"
            )

            KotlinCode(
                code = """
                    //Three-Layer Architecture
                    
                    ┌───────────────────────────────────────────────┐
                    │   PLATFORM-SPECIFIC CODE (UI Layer)           │
                    │   Android: Jetpack Compose/XML                │
                    │   iOS: SwiftUI/UIKit                          │
                    └─────────────────────────────────in a shared module

                    2. **The Kotlin compiler has multiple backends** — one for each platform 
                       - **Kotlin compiler** → produces NATIVE code for each platform
                       - Compiler produces → Native ARM64 machine code via LLVM

                    3. **Each backend compiles your code to native format**:
                       - **Android** → JVM bytecode (`.class` files → DEX bytecode)
                       - **iOS** → ARM64/x86_64 machine code (native binary)
                       - **JavaScript** → JavaScript source code
                       - **Desktop** → Native executables (via LLVM)
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMP is based on the capability of Kotlin to be converted and become fully operative on different platform such as Android or iOS.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the Android environment Kotlin is converted into bytecode and then executed by the JVM.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Kotlin Compiler has 2 different layers:",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Frontend",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Backend",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Frontend layer converts the code into IR (Intermediate Representation). Parse Kotlin source code into an abstract syntax tree (AST). Perform semantic checks on AST to ensure that the code complies with the Kotlin language specification and perform type inference and checking. Convert AST to an intermediate representation for further processing by different compilation backends.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Backend layer converts this IR into the corresponding executable code depending on the platform. This layer is where the magic happens. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example in Android it gets converted into Java Bytecode, for iOS in native machine code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This conversion by IR to specific platform code it is possible thanks to Kotlin/Native compiler. This compiler uses a LLVM toolchain to convert Kotlin primitives, classes, and different programming structures into the specific one.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "JVM backend: Converts the intermediate representation into JVM bytecode to run on JVM platforms (such as Android).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Native backend: Converts the intermediate representation to LLVM bitcode, and then generates native binary code suitable for different platforms (such as iOS, Linux, Windows, etc.) through the LLVM tool chain.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "JavaScript backend: Converts the intermediate representation into JavaScript code to run in a browser or Node.js environment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "WebAssembly backend: Converts the intermediate representation into WebAssembly code to run in a browser or other WebAssembly-enabled environment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin -> Intermediate Representation (IR) -> Native executable code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //ios
                    - No bridge overhead at runtime
                    - Direct interoperability with Swift/Objective-C through generated headers
                    - The shared module runs as native ARM64 code
                    
                    Kotlin Source (.kt)
                    │
                    ├──▶ Android ─
                    │    └── Android, CMP uses the native Jetpack Compose engine. It draws directly to the Android rendering pipeline.
                    │
                    ├──▶ Kotlin/JVM (Android) ──▶ .class / .dex
                    │    └── Compiles to standard Java bytecode for Android and backend services.
                    │
                    ├──▶ Kotlin/Native (iOS) ──▶ LLVM IR ──▶ .xcframewor 
                    │    └── When you target iOS, Kotlin code is compiled via the Kotlin/Native backend using LLVM to produce a native binary (.framework or .xcframework). CMP uses Skia - the same hardware-accelerated 2D graphics library that powers Chrome, Flutter, and Android itself. On iOS specifically, Skia is wired into Metal, Apple's low-level GPU API.
                    │ 
                    ├──▶ Kotlin/Wasm
                    │    └── By compiling to WebAssembly, KMP can run at near-native speeds in the browser, bypassing the performance bottlenecks of JavaScript. it renders via Skia to an HTML canvas, again with hardware acceleration.
                    """.trimIndent()

            )

            SpanText(
                text = "What is Kotlin/Native, and how does it relate to Kotlin Multiplatform?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin/Native is a technology for compiling Kotlin code to native binaries, which can run without a virtual machine. It includes an LLVM-based backend for the Kotlin compiler and a native implementation of the Kotlin standard library.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin/Native is primarily designed to allow compilation for platforms where virtual machines are not desirable or possible, such as embedded devices and iOS. It is particularly suitable when you need to produce a self-contained program that does not require an additional runtime or virtual machine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, in mobile applications, shared code written in Kotlin is compiled to JVM bytecode for Android with Kotlin/JVM and compiled to native binaries for iOS with Kotlin/Native. It makes the integration with Kotlin Multiplatform seamless on both platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is the difference between Kotlin Multiplatform and Kotlin Native?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Multiplatform is a framework for sharing code across platforms, while Kotlin Native is a compiler that turns Kotlin code into native binaries. Kotlin Multiplatform often uses Kotlin Native for iOS and other native targets.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Can Kotlin Multiplatform work without Kotlin Native?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Partially. It can target JVM and JS without Kotlin Native, but for iOS or native platforms, Kotlin Native is required.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Targets configure KMP",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "applyDefaultHierarchyTemplate()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Configures a default shared source set hierarchy, simplifying setup by establishing common dependencies and configurations across platform targets, like Android, iOS, and JVM.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It enables code reuse by creating shared source sets with logical hierarchies.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "androidTarget()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Adds Android as a target platform, allowing the KMP project to compile Android-specific code. For library modules, publishLibraryVariants(\"release\") can be used to specify which build variants are published.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "iosX64(), iosArm64(), iosSimulatorArm64()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Configures targets for different iOS CPU architectures, supporting iOS on x64 (for simulator), arm64 (for devices), and Simulator Arm64.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The baseName field can specify a framework name, like “ComposeApp,” for the output.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "macosX64(), macosArm64()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Targets for macOS, allowing the code to be compiled for macOS on both x64 (Intel) and Arm64 (Apple Silicon) architectures. ",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This setup supports building shared code for macOS applications.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "jvm()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compiles the shared code for the Java Virtual Machine (JVM), which is compatible with desktop and Android applications, providing easy integration with Java libraries and environments.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "wasmJs()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Configures the project to target WebAssembly with JavaScript interop, enabling shared Kotlin code to execute as WebAssembly (WASM) in a web environment.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is especially useful for creating performant web apps.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "linuxX64(), linuxArm64()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Targets for Linux on x64 and Arm64 architectures, allowing the shared code to run on Linux environments, which is helpful for server-side applications or embedded Linux devices.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "cinterop",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://medium.com/@shivathapaa
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The cinterop tool is Kotlin/Native’s bridge to native code. It’s invoked as part of the Kotlin/Native compilation process and generates Kotlin bindings by analyzing C/Objective-C headers. These bindings are compiled into the .klib artifact and provide type-safe Kotlin APIs with automatic memory management.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For Swift, the flow works like this: Swift Code (@objc) → Objective-C Bridge → C Headers → CInterop Tool → Kotlin Bindings",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin/Native doesn’t talk directly to Swift. It talks to Objective-C, which is C-based. Since Swift can expose classes to Objective-C via @objc annotations, we get:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Swift code: marked with @objc → exposed to Objective-C runtime",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Objective-C headers (.h files) → describe the API in C terms",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CInterop tool reads headers → generates Kotlin code automatically",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Kotlin code calls generated bindings → executes Swift code at runtime",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The cinterop tool analyzes C/Objective-C headers and produces straightforward mappings of types, functions, and constants into Kotlin. All the complex interop code is generated for us, we just need to provide the headers and configuration.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Advantages of Kotlin multimodule",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Code Reusability",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "KMM’s primary strength is maximizing code sharing between platforms.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You write vital components like business logic, network interactions, and data models once.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This significantly accelerates development time and reduces the potential for platform-specific discrepancies.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Native Performance",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Shared KMM code is compiled into native binaries for each platform.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Consequently, you don’t compromise on performance as you would with hybrid frameworks that rely on web views or JavaScript bridges.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Your app benefits from the full power of the underlying platforms.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Improved Code Maintainability",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Kotlin’s popularity is rising due to its conciseness, safety features, and interoperability with Java.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "KMM enables existing Kotlin developers to expand their skills into the iOS development realm.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Code Sharing",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Share business logic across platforms, reducing duplication and improving maintainability.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Interoperability",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Seamless integration with existing native code, making it easy to work with existing projects.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "KMP folder structure",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "androidLibMain: Android platform-specific KMP logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "commonMain: KMP business logic that does not require any platform-specific code / API.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "iosMain: iOS platform-specific KMP logic that has the ability to interact with Apple APIs (ie: UIKit, GCD, etc…)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "main: Contains a AndroidManifest.xml file, which defines / as an Android Library. In many ways, it’s like an Xcode.plist.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Targets",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Kotlin/JS target-platform encompasses browser and node.js targets. This means we can build a web app and a server if we declare these targets. While Kotlin/Native target-platform includes macOs, iOS, linux and many more.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "KMMBridge",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMMBridge is a Gradle plugin that allows you to publish Xcode XCFrameworks from your Kotlin Multiplatform projects. Developers using Xcode can then directly consume these CFrameworks using Swift Package Manager (SPM)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMMBridge is a tool that helps you publish iOS XCFramework binaries from Kotlin Multiplatform. The tool has seen multiple iterations over the past few years. This version simplifies the configuration and usage of KMMBridge, with a primary focus on Swift Package Manager (SPM).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMMBridge is a set of Gradle tools that integrate with your Kotlin multimodule  project to assist with building and publishing KMM Xcode Frameworks. It is built for the Internal SDK Flow, but you can also use it to publish public SDKs as well.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It allows publication on different backends and its use through CocoaPods or Swift Package Manager.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "XCFramework",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An XCFramework is a bundle containing pre-compiled binaries for multiple architectures: real devices (ios-arm64), Apple Silicon simulators (ios-arm64-simulator ), and Intel simulators (ios-x86_64-simulator).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Binary SPM target",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Swift Package can declare a binaryTarget that points to a remote zip containing an XCFramework. iOS developers consume it exactly like a source-based Swift package — via Xcode's Add Package Dependencies dialog — without any Kotlin or Gradle tooling.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How Compose Multiplatform Actually Draws on iOS?",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It doesn't rely on UIKit to draw your UI. Ported to Kotlin through a layer called Skiko. This means your UI is rendered with one of the most powerful graphics engines on the planet — on every platform you target.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Your @Composable functions describe the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Skia/Skiko takes over and draws directly to the canvas.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A lightweight UIKitView container holds that canvas.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Metal GPU delivers the final output to the screen. UIKit is just a thin shell. Skia owns every pixel — with full control.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "SKIE",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "One of the main disadvantages of Kotlin Multiplatform is the lack of direct interaction with Swift. Without this, Swift can only communicate with Kotlin indirectly, through Objective-C. This approach, although functional, has many limitations and causes Kotlin to lose support for many of its language features.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SKIE is a specialized plugin of the Kotlin native compiler that recovers support for some of these features by modifying the Xcode framework produced by the Kotlin compiler.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Thanks to this, it is not necessary to change the way you distribute and consume your Kotlin Multiplatform frameworks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SKIE is a powerful and essential tool for Kotlin Multiplatform developers. It allows them to safely and efficiently export interfaces between different platforms. With features that automate the export process and ensure type compatibility, SKIE significantly improves the development experience. In addition, it facilitates the creation of robust and easy-to-maintain multi-platform applications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    sealed class Status {
                        object Loading : Status()
                        data class Error(val message: String) : Status()
                        data class Success(val result: SomeData) : Status()
                    }

                    //Swift without SKIE
                    func updateStatus(status: Status) {
                        switch status {
                        case _ as Status.Loading:
                            showLoading()
                        case let error as Status.Error:
                            showError(message: error.message)
                        case let success as Status.Success:
                            showResult(data: success.result)
                        default:
                            fatalError("Unknown status")
                        }
                    }

                    //Swift with SKIE
                    func updateStatus(status: Status) {
                        switch onEnum(of: status) {
                        case .loading:
                            showLoading()
                        case .error(let error):
                            showError(message: error.message)
                        case .success(let success):
                            showResult(data: success.result)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModel androidx lifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/seabat/ramen-note.git
            KotlinCode(
                code = """
                    interface ShopViewModelContract {
                        val shopImage: StateFlow<ByteArray?>
                        fun loadImage(shop: Shop)
                    }

                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.viewModelScope

                    class ShopViewModel(
                        private val loadImageUseCase: LoadImageUseCaseContract
                    ) : ViewModel(), ShopViewModelContract {
                        
                        private val _shopImage = MutableStateFlow<ByteArray?>(null)
                        override val shopImage: StateFlow<ByteArray?> = _shopImage.asStateFlow()
                        
                        /*
                        var query by mutableStateOf( value = "")
                            private set
                        */
                        
                        override fun loadImage(shop: Shop) {
                          
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Want to debug your 𝗶𝗢𝗦 𝗞𝗠𝗣 from 𝗔𝗻𝗱𝗿𝗼𝗶𝗱 𝗦𝘁𝘂𝗱𝗶𝗼?",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.DEBUG_SETTING_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "KMM Android Studio iOS Debug Setting"
            )

            SpanText(
                text = "KMM New Project Create",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "1. https://kmp.jetbrains.com/",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "2. Android studio plugin using KMM Plugin",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "3. https://plugins.jetbrains.com/plugin/25221-quickprojectwizard?noRedirect=true",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Terminal",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    chmod +x gradlew //gradlew permission

                    ./gradlew :composeApp:run
                    ./gradlew run
                    ./gradlew :desktop:run
                    ./gradlew :jsApp:jsBrowserDevelopmentRun
                    ./gradlew jsBrowserRun --continuous
                    ./gradlew packageDistributionForCurrentOS
                    For iOS simulator: ./gradlew :shared:kittens:iosX64MainBinaries
                    For iOS device: ./gradlew :shared:kittens:iosArm64MainBinaries
                    ./gradlew runNative
                    ./gradlew wasmJsBrowserRun
                    ./gradlew wasmJsBrowserRun -t
                    ./gradlew -q :tasks --all

                    ./gradlew :composeApp:wasmJsBrowserDevelopmentRun
                    ./gradlew :webApp:jsBrowserDevelopmentExecutableDistribution
                    ./gradlew :webApp:jsBrowserDistribution
                    ./gradlew :wasmApp:wasmBrowserDevelopmentRun
                    ./gradlew :wasmApp:wasmBrowserDevelopmentExecutableDistribution
                    ./gradlew :wasmApp:wasmJsBrowserDistribution
                    ./gradlew :composeApp:wasmJsBrowserDevelopmentRun --continue
                    """.trimIndent()
            )

            SpanText(
                text = "LifecycleRegistry",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    private val lifecycle: LifecycleRegistry = LifecycleRegistry()
                    private val root: RootComponent = RootComponentFactory(componentContext = DefaultComponentContext(lifecycle))

                    @Suppress("FunctionName")
                    fun RootComponentFactory(
                        componentContext: ComponentContext,
                    ): RootComponent { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/djkovrik/Tackle
                    //androidMain

                    import android.content.Context
                    import com.seiko.imageloader.ImageLoader
                    import com.seiko.imageloader.cache.memory.maxSizePercent
                    import com.seiko.imageloader.component.setupDefaultComponents
                    import com.seiko.imageloader.intercept.bitmapMemoryCacheConfig
                    import com.seiko.imageloader.intercept.imageMemoryCacheConfig
                    import com.seiko.imageloader.intercept.painterMemoryCacheConfig
                    import com.seiko.imageloader.option.androidContext
                    import okio.Path.Companion.toOkioPath

                    fun generateImageLoader(context: Context): ImageLoader {
                        return ImageLoader {
                            options {
                                androidContext(context.applicationContext)
                            }
                            components {
                                setupDefaultComponents()
                            }
                            interceptor {
                                bitmapMemoryCacheConfig {
                                    maxSizePercent(context = context, percent = 0.25)
                                }
                                imageMemoryCacheConfig {
                                    maxSize(size = 50)
                                }
                                painterMemoryCacheConfig {
                                    maxSize(size = 50)
                                }
                                diskCacheConfig {
                                    directory(context.cacheDir.resolve("image_cache").toOkioPath())
                                    maxSizeBytes(size = 512L * 1024 * 1024)
                                }
                            }
                        }
                    }

                    setContent {
                        TackleTheme {
                            CompositionLocalProvider(
                                LocalImageLoader provides generateImageLoader(this)
                            ) {
                                    RootContent(component = root)
                              }
                        }
                    }

                    //iosMain
                    fun generateImageLoader(): ImageLoader {
                        return ImageLoader {
                            components {
                                setupDefaultComponents()
                            }
                            interceptor {
                                bitmapMemoryCacheConfig {
                                    maxSize(size = 32 * 1024 * 1024)
                                }
                                imageMemoryCacheConfig {
                                    maxSize(size = 50)
                                }
                                painterMemoryCacheConfig {
                                    maxSize(size = 50)
                                }
                                diskCacheConfig {
                                    directory(getCacheDir().toPath().resolve(child = "image_cache"))
                                    maxSizeBytes(size = 512L * 1024 * 1024)
                                }
                            }
                        }
                    }

                    private fun getCacheDir(): String {
                        return NSSearchPathForDirectoriesInDomains(
                            NSCachesDirectory,
                            NSUserDomainMask,
                            true,
                        ).first() as String
                    }

                    @Suppress("FunctionName")
                    fun MainViewController(): UIViewController =
                        ComposeUIViewController {
                            TackleTheme {
                                CompositionLocalProvider(
                                    LocalImageLoader provides generateImageLoader()
                                ) {
                                    RootContent(component = root)
                                }
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "Improve Code Quality",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DeteKt",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kover",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Access resources",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    typealias ResString = package_name.generated.resources.Res.string

                    typealias ResDrawable = package_name.generated.resources.Res.drawable
                    """.trimIndent()
            )

            SpanText(
                text = "DateTime Formatter",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val formatter = DateTimeFormatter.ofPattern("dd MMM")
                    """.trimIndent()
            )

            SpanText(
                text = "Platform Specific Code",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // Shared module (commonMain)
                    interface ApiService {
                        suspend fun fetchUsers(): List<User>
                    }

                    class ApiServiceImpl : ApiService {
                        override suspend fun fetchUsers(): List<User> {
                            // Simulated API call
                            return listOf(User("Alice"), User("Bob"))
                        }
                    }

                    data class User(val name: String)
                    
                    //androidMain
                    val apiService: ApiService = ApiServiceImpl()
                    lifecycleScope.launch {
                        val users = apiService.fetchUsers()
                        users.forEach { Log.d("User", it.name) }
                    }
                    
                    //iosMain - Bridged via Kotlin/Native
                    let users = SharedModule.ApiServiceImpl().fetchUsers()
                    print(users.map { $0.name })
                    """.trimIndent()
            )

            SpanText(
                text = "App language",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/MuhammetKonukcu/LitLounge
            //https://github.com/thejohnsondev/PassGuard-Multiplatform
            KotlinCode(
                code = """
                    //commonMain
                    enum class AppLang(
                        val code: String,
                        val stringRes: StringResource
                    ) {
                        English("en", Res.string.en),
                        Turkish("tr", Res.string.tr)
                    }
                    
                    import androidx.compose.runtime.Composable
                    interface AppLocaleManager {
                        fun getLocale(): String
                    }

                    @Composable
                    expect fun rememberAppLocale(): AppLang
                    
                    //iosMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import platform.Foundation.NSLocale
                    import platform.Foundation.currentLocale
                    import platform.Foundation.languageCode

                    class IosAppLocaleManager : AppLocaleManager {
                        override fun getLocale(): String {
                            val nsLocale =
                                NSLocale.currentLocale.languageCode
                            return nsLocale
                        }
                    }

                    @Composable
                    actual fun rememberAppLocale(): AppLang {
                        val nsLocale = IosAppLocaleManager().getLocale()
                        return remember(nsLocale) {
                            when (nsLocale) {
                                "tr" -> AppLang.Turkish
                                else -> AppLang.English
                            }
                        }
                    }
                    
                    //androidMain
                    import android.content.Context
                    import android.os.Build
                    import android.os.LocaleList
                    import androidx.appcompat.app.AppCompatDelegate
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import androidx.compose.ui.platform.LocalContext

                    class AndroidAppLocaleManager(
                        private val context: Context,
                    ) : AppLocaleManager {

                        private val localManager: Any? by lazy {
                            val cls = try {
                                Class.forName("android.app.LocaleManager")
                            } catch (_: ClassNotFoundException) {
                                null
                            }
                            cls?.let {
                                context.getSystemService(Context.LOCATION_SERVICE)
                                context.getSystemService("locale")
                            }
                        }

                        override fun getLocale(): String {
                            return if (Build.VERSION.SDK_INT >= Build.VERSION_CODES.TIRAMISU && localManager != null) {
                                val appLocales = localManager!!.javaClass
                                    .getMethod("getApplicationLocales")
                                    .invoke(localManager) as LocaleList
                                appLocales.get(0)?.toLanguageTag()?.substringBefore("-") ?: "en"
                            } else {
                                val tags = AppCompatDelegate.getApplicationLocales()
                                    .toLanguageTags()
                                    .split("-")
                                tags.firstOrNull().orEmpty().ifEmpty { "en" }
                            }
                        }
                    }

                    @Composable
                    actual fun rememberAppLocale(): AppLang {
                        val context = LocalContext.current
                        val locale = AndroidAppLocaleManager(context).getLocale()
                        return remember(locale) {
                            locale.toApLang()
                        }
                    }

                    private fun String?.toApLang(
                    ): AppLang = when (this) {
                        "tr" -> AppLang.Turkish
                        else -> AppLang.English
                    }
                    
                    val LocalAppLocalization = compositionLocalOf {
                        AppLang.English
                    }

                    @Composable
                    @Preview
                    fun MainScreen() {
                        val currentLanguage = rememberAppLocale()
                        CompositionLocalProvider(LocalAppLocalization provides currentLanguage) {
                            AppTheme {
                                Surface(
                                    modifier = Modifier.fillMaxSize().navigationBarsPadding(),
                                    color = MaterialTheme.colorScheme.background
                                ) {
                                    
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Read File From Resource",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     @OptIn(InternalResourceApi::class)
                      suspend fun loadFoodFromResource(): List<FoodItem> {
                        val bytes = Res.readBytes("files/food_nutrition.json")
                        val text = bytes.decodeToString()
                        return Json.decodeFromString<List<FoodItem>>(text)
                     }
                    """.trimIndent()
            )

            SpanText(
                text = "viewModelFactory",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/rainxchzed/FebruaryMiniChallenges.git
            KotlinCode(
                code = """
                    //Kotlin Multiplatform 
                    
                    //1
                    class FoodViewModel : ViewModel() { 
                        companion object {
                            val foodViewModelFactory = viewModelFactory {
                                initializer {
                                    FoodViewModel()
                                }
                            }
                        }
                    }
                    
                    val foodViewModel: FoodViewModel = viewModel(factory = foodViewModelFactory)
                    
                    //2
                    //androidMain
                    viewModel = viewModel(factory = viewModelFactory {
                        addInitializer(MissedMessagesViewModel::class, initializer = {
                            MissedMessagesViewModel(AndroidMissedMessagedRepository())
                        })
                    })
                    
                    //iosMain
                    fun MainViewController() = ComposeUIViewController {
                        App(viewModel = viewModel(factory = viewModelFactory {
                            addInitializer(StillConnectedViewModel::class, initializer = {
                                StillConnectedViewModel(IOSConnectivityManager())
                            })
                        }))
                    }
                    """.trimIndent()
            )

            //https://github.com/mheerwaarden/CMPEventDemo.git
            KotlinCode(
                code = """
                    object AppViewModelProvider : KoinComponent {
                        val Factory = viewModelFactory {
                            /* region Event ViewModels */
                            val appContainer = EventDemoApplication.container
                            initializer {
                                EventOverviewViewModel(
                                    appContainer.eventRepository
                                )
                            }
                        }
                    }
                    
                    object EventDemoApplication {
                        /**
                         * AppContainer instance used by the rest of classes to obtain dependencies
                         */
                        var container: AppContainer = AppDataContainer()
                    }
                    
                    interface AppContainer {
                        val eventRepository: EventRepository
                    }
                    
                    /**
                     * [AppContainer] implementation that provides instances of the repositories of the model objects
                     */
                    class AppDataContainer : AppContainer {
                        init {
                            println("AppDataContainer: init")
                        }

                        override val eventRepository: EventRepository by lazy {
                            DummyEventRepository()
                        }
                    }
                    
                    interface EventRepository : DataLoadingRepository { }
                    
                    interface DataLoadingRepository { }
                    
                    @Composable
                    fun EventScreen(
                        eventViewModel: EventViewModel = viewModel(factory = AppViewModelProvider.Factory)
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Injecting Android Context in Compose Multiplatform with Koin / Context Koin",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/landomen/KMPSpaceFlightNews (koin context)
            //https://github.com/romreviewer/NoteItUP.git (koin context, Swift LAContext())
            KotlinCode(
                code = """
                    import org.koin.android.ext.koin.androidContext
                    
                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)

                            setContent {
                                // provide KoinAppDeclaration
                                App({
                                    androidContext(this@MainActivity.applicationContext)
                                })
                            }
                        }
                    }
                    
                    import android.content.Context
                    import app.cash.sqldelight.db.SqlDriver
                    import app.cash.sqldelight.driver.android.AndroidSqliteDriver

                    class AndroidDatabaseDriverFactory(private val context: Context): DatabaseDriverFactory {

                        override fun createDriver(): SqlDriver {
                            return AndroidSqliteDriver(AppDatabase.Schema, context, "articles.db")
                        }
                    }
                    
                    actual val platformModule: Module
                        get() = module {
                            single<DatabaseDriverFactory> { AndroidDatabaseDriverFactory(context = get()) }
                            single<ShareService> { AndroidShareService(context = get()) }
                        }
                        
                    //iosMain
                    import platform.LocalAuthentication.LAContext
                    
                    //You perform biometric authentication in your app using an LAContext instance, which brokers interaction between your app and the Secure Enclave. 
                    private val context = LAContext()
                    """.trimIndent()
            )

            SpanText(
                text = "koin Instance",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/shaka-it/kmp-multimodule-sample.git
            KotlinCode(
                code = """
                    ---- common ---- 
                    import org.koin.core.Koin

                    object Inject {
                        private var _koin: Koin? = null
                        val koin: Koin get() = requireNotNull(_koin)

                        fun init(koin: Koin) {
                            _koin = koin
                        }

                        inline fun <reified T> instance(): T = koin.get()
                    }
                    
                    import org.koin.core.Koin
                    import org.koin.core.component.KoinComponent

                    /**
                     * Used to create Decompose components, creation of components are implemented as extension functions
                     */
                    class ComponentFactory(private val localKoin: Koin) : KoinComponent {

                        override fun getKoin(): Koin = localKoin
                    }
                    
                    object PlatformSDK {
                        fun init(
                            configuration: PlatformConfiguration
                        ) {
                            val umbrellaModule = module {
                                single {
                                    configuration
                                }
                            }
                            val modules = listOf(
                               
                            )

                            Inject.init(
                                koin = startKoin {
                                    modules(modules = modules)
                                }.koin,
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Platform Check",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun Platform.isApplePlatform(): Boolean = this == Platform.IOS || this == Platform.MACOS

                    fun Platform.isAndroid(): Boolean = this == Platform.ANDROID
                    
                    private fun Platform.toArrowBackIcon(): ImageVector = if (isApplePlatform()) {
                        Icons.AutoMirrored.Filled.ArrowBackIos
                    } else {
                        Icons.AutoMirrored.Filled.ArrowBack
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "KMP Maps",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A universal map component for Compose Multiplatform applications, offering seamless integration with native map APIs on both Android and iOS platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It uses Google Maps SDK on Android and Apple Maps (MapKit) on iOS. We also added Google Maps support for iOS, the possibility to display GeoJSON data, and custom markers are on their way. The location permission handling is built in, and user interactions like clicks, camera movements, and gestures are handled.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Validation koin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/AnatoliiChub/HRAM.git
            KotlinCode(
                code = """
                    private const val MIN_NAME_LENGTH = 3
                    private const val MAX_NAME_LENGTH = 50

                    class ActivityNameValidation {
                        operator fun invoke(name: String) = when {
                            name.isBlank() -> Res.string.activity_screen_name_validation_empty
                            name.firstOrNull()?.isDigit() ?: false -> Res.string.activity_screen_name_validation_start_digit
                            name.length < MIN_NAME_LENGTH -> Res.string.activity_screen_name_validation_too_short
                            name.length > MAX_NAME_LENGTH -> Res.string.activity_screen_name_validation_too_long
                            else -> null
                        }
                    }
                    
                    @Module
                    @Configuration
                    class UtilsModule {
                        @Factory
                        fun provideActivityNameValidationUseCase() = ActivityNameValidation()
                    }
                    
                    @KoinApplication(
                        modules = [
                            UtilsModule::class,
                        ]
                    )
                    object AppModule
                    """.trimIndent()
            )

            SpanText(
                text = "Dispatchers using factory, interface and qualifier",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    sealed interface AppDispatchers {
                        val io: CoroutineDispatcher
                        val main: CoroutineDispatcher
                        val default: CoroutineDispatcher

                        companion object : AppDispatchers {
                            override val io: CoroutineDispatcher
                                get() = Dispatchers.IO
                            override val main: CoroutineDispatcher
                                get() = Dispatchers.Main
                            override val default: CoroutineDispatcher
                                get() = Dispatchers.Default

                        }
                    }
                    
                    val commonModule = module {
                        single<AppDispatchers> { AppDispatchers }
                    }
                    """.trimIndent()
            )

            //https://github.com/RznNike/DemoKMP.git (Good API Calling (webSocketManager, ktorfit, Common UseCase, Common ViewModel))
            KotlinCode(
                code = """
                       internal val appModule = module {

                           single<CoroutineScopeProvider> {
                               object : CoroutineScopeProvider {
                                   override val ui = MainScope()
                                   override val default = CoroutineScope(Dispatchers.Default)
                                   override val io = CoroutineScope(Dispatchers.IO)
                                   override val unconfined = CoroutineScope(Dispatchers.Unconfined)
                               }
                           }

                           single<DispatcherProvider> {
                               object : DispatcherProvider {
                                   override val ui = Dispatchers.Main
                                   override val default = Dispatchers.Default
                                   override val io = Dispatchers.IO
                                   override val unconfined = Dispatchers.Unconfined
                               }
                           }
                       }
                       
                       interface CoroutineScopeProvider {
                           val ui: CoroutineScope
                           val default: CoroutineScope
                           val io: CoroutineScope
                           val unconfined: CoroutineScope
                       }
                       
                       interface DispatcherProvider {
                           val ui: CoroutineDispatcher
                           val default: CoroutineDispatcher
                           val io: CoroutineDispatcher
                           val unconfined: CoroutineDispatcher
                       }
                    """.trimIndent()
            )

            //1. https://github.com/InonELGABSI/TopOut
            //2. https://github.com/SebaUbuntu/OpenOBD
            KotlinCode(
                code = """
                    //1
                    factory { (scope: CoroutineScope) -> }
                    single<CoroutineScope> { CoroutineScope(SupervisorJob() + Dispatchers.Default) }
                    
                    //2
                    single { Dispatchers.IO }
                    single { CoroutineScope(SupervisorJob() + get<CoroutineDispatcher>()) }
                    """.trimIndent()
            )

            //https://github.com/MarcinMoskala/albert.git
            KotlinCode(
                code = """
                    val appModule = module {
                        // Background scope for repository
                        single<CoroutineScope>(createdAtStart = true) {
                            val handler = CoroutineExceptionHandler { _, e ->
                                get<ErrorHandler>().handleError(e)
                            }
                            CoroutineScope(SupervisorJob() + Dispatchers.Default + handler)
                        }
                        
                        single { SnackbarController() }
                        single<ErrorHandler> { ErrorHandlerImpl(get()) }
                        
                        /*
                        single(named("ApplicationScope")) {
                            CoroutineScope(SupervisorJob( ) + Dispatchers.Default)
                        }*/
                    }
                    
                    class SnackbarController {
                        private val _messages = Channel<String>(Channel.UNLIMITED)
                        val messages = _messages.receiveAsFlow()

                        fun showMessage(message: String) {
                            _messages.trySend(message)
                        }

                        fun showError(error: Throwable) {
                            val message = error.message ?: "An unknown error occurred"
                            showMessage(message)
                        }
                    }
                    
                    interface ErrorHandler {
                        fun handleError(error: Throwable)
                    }

                    class ErrorHandlerImpl(
                        private val snackbarController: SnackbarController
                    ) : ErrorHandler {
                        override fun handleError(error: Throwable) {
                            error.printStackTrace()
                            snackbarController.showError(error)
                        }
                    }
                    
                    abstract class BaseViewModel(
                        private val errorHandler: ErrorHandler
                    ) : ViewModel() {

                        private val coroutineExceptionHandler = CoroutineExceptionHandler { _, throwable ->
                            errorHandler.handleError(throwable)
                        }

                        protected val viewModelScope: CoroutineScope =
                            CoroutineScope(SupervisorJob() + Dispatchers.Main.immediate + coroutineExceptionHandler)

                        override fun onCleared() {
                            viewModelScope.coroutineContext.job.cancelChildren()
                        }
                    }
                    
                    class ExactTextStepViewModel(
                        errorHandler: ErrorHandler
                    ) : BaseViewModel(errorHandler) {
                        
                    }
                    
                    class ProgressSynchronizer(
                        private val ioDispatcher: CoroutineDispatcher,
                    ) {
                        suspend fun synchronizeWithRemote() {
                            withContext(ioDispatcher) {
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/longdt57/Kotlin-Multiplatform.git
            KotlinCode(
                code = """
                    /**
                     * A container for commonly used [kotlinx.coroutines.CoroutineDispatcher]s in the application.
                     *
                     * [AppDispatchers] provides a centralized way to inject and manage coroutine dispatchers across different layers
                     * of the app (e.g., domain, data, presentation). This is especially useful for improving testability and
                     * ensuring consistent thread usage in Kotlin Multiplatform projects.
                     *
                     * ### Why Use This?
                     * - Decouple coroutine usage from hardcoded dispatchers (like `Dispatchers.IO`)
                     * - Enable easy replacement of dispatchers in tests (e.g., with `StandardTestDispatcher`)
                     * - Promote a clean architecture by passing execution context explicitly
                     *
                     * @property io Dispatcher optimized for offloading blocking I/O tasks (e.g., network, file, or database).
                     * @property main Dispatcher intended for UI-related work (e.g., Compose recomposition or ViewModel updates).
                     * @property default Dispatcher optimized for CPU-intensive tasks (e.g., sorting, parsing, or business logic).
                    */
                    
                    //1
                    data class AppDispatchers(
                        val io: CoroutineDispatcher = Dispatchers.IO,
                        val main: CoroutineDispatcher = Dispatchers.Main,
                        val default: CoroutineDispatcher = Dispatchers.Default,
                    ) 
                     
                    //2 
                    import kotlinx.coroutines.CoroutineDispatcher

                    interface DispatchersProvider {
                        val io: CoroutineDispatcher
                        val main: CoroutineDispatcher
                        val default: CoroutineDispatcher
                    }
                    
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO

                    class DispatchersProviderImpl constructor() : DispatchersProvider {
                        override val io = Dispatchers.IO
                        override val main = Dispatchers.Main
                        override val default = Dispatchers.Default
                    }
                    
                    single<DispatchersProvider> { DispatchersProviderImpl() }
                    """.trimIndent()
            )

            //https://github.com/by-tezov/tuucho
            KotlinCode(
                code = """
                    import kotlinx.coroutines.CoroutineName
                    import kotlinx.coroutines.CoroutineScope
                    import kotlinx.coroutines.Deferred
                    import kotlinx.coroutines.Job
                    import kotlinx.coroutines.SupervisorJob
                    import kotlinx.coroutines.async
                    import kotlin.coroutines.CoroutineContext

                    interface CoroutineContextProtocol {
                        val context: CoroutineContext
                        val job: Job
                        val scope: CoroutineScope

                        fun <T> async(
                            onException: ((e: Throwable) -> Unit)? = null,
                            block: suspend CoroutineScope.() -> T,
                        ): Deferred<T>

                        suspend fun <T> await(block: suspend CoroutineScope.() -> T): T
                    }

                    open class CoroutineContext(
                        name: String,
                        override val context: CoroutineContext,
                    ) : CoroutineContextProtocol {

                        override val job: Job = SupervisorJob()

                        override val scope: CoroutineScope = CoroutineScope(context + job + CoroutineName(name))

                        override fun <T> async(
                            onException: ((e: Throwable) -> Unit)?,
                            block: suspend CoroutineScope.() -> T,
                        ): Deferred<T> {
                            val deferred = scope.async(block = block)
                            deferred.invokeOnCompletion { e: Throwable? ->
                                if (e != null) {
                                    onException?.invoke(e) ?: throw e
                                }
                            }
                            return deferred
                        }

                        override suspend fun <T> await(block: suspend CoroutineScope.() -> T): T {
                            return scope.async(block = block).await()
                        }
                    }
                    
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO

                    class CoroutineScopes(
                        override val database: CoroutineContextProtocol = object :
                            CoroutineContext("Database", Dispatchers.IO) {},
                        override val network: CoroutineContextProtocol = object :
                            CoroutineContext("Network", Dispatchers.IO) {},
                        override val parser: CoroutineContextProtocol = object :
                            CoroutineContext("Parser", Dispatchers.Default) {},

                        override val renderer: CoroutineContextProtocol = object :
                            CoroutineContext("Renderer", Dispatchers.Default) {},

                        override val navigation: CoroutineContextProtocol = object :
                            CoroutineContext("Navigation", Dispatchers.Default) {},
                        override val event: CoroutineContextProtocol = object :
                            CoroutineContext("Event", Dispatchers.Default) {},
                        override val useCase: CoroutineContextProtocol = object :
                            CoroutineContext("UseCase", Dispatchers.Default) {},

                        override val default: CoroutineContextProtocol = object :
                            CoroutineContext("Default", Dispatchers.Default) {},
                        override val main: CoroutineContextProtocol = object :
                            CoroutineContext("Main", Dispatchers.Main) {},
                        override val io: CoroutineContextProtocol = object :
                            CoroutineContext("IO", Dispatchers.IO) {},
                    ) : CoroutineScopesProtocol
                    
                    interface CoroutineScopesProtocol {
                        val database: CoroutineContextProtocol
                        val network: CoroutineContextProtocol
                        val parser: CoroutineContextProtocol
                        val renderer: CoroutineContextProtocol
                        val navigation: CoroutineContextProtocol
                        val event: CoroutineContextProtocol
                        val useCase: CoroutineContextProtocol

                        val default: CoroutineContextProtocol
                        val main: CoroutineContextProtocol
                        val io: CoroutineContextProtocol
                    }
                    
                    object SystemKmmModules {
                        internal operator fun invoke() = listOf(
                            module {
                                single<CoroutineScopesProtocol> {
                                    CoroutineScopes()
                                }
                            }
                        )
                    }
                    
                    object StartKoinModules {
                        @Composable
                        internal operator fun invoke(
                            moduleDeclaration: ModuleDeclaration,
                            content: @Composable () -> Unit,
                        ) = KoinApplication(application = {
                            allowOverride(override = false)
                            modules(module { moduleDeclaration() })
                            modules(SystemKmmModules())
                        }, content = content)
                    }
                    
                    @Composable
                    fun AppScreen(
                        moduleDeclaration: ModuleDeclaration
                    ) = StartKoinModules(moduleDeclaration) { }

                    """.trimIndent()
            )

            //https://github.com/beHqo/MockStore
            KotlinCode(
                code = """
                    //dispatchers factory
                    
                    /**
                    * Koin module for provisioning Kotlin Coroutine dependencies for injection
                    */
                    
                    //CoroutineModule.kt
                    const val MAIN_DISPATCHER = "MainDispatcher"
                    const val DEFAULT_DISPATCHER = "DefaultDispatcher"
                    const val IO_DISPATCHER = "IODispatcher"

                    val dispatchersModule = module {
                        factory(named(MAIN_DISPATCHER)) { Dispatchers.Main }
                        factory(named(DEFAULT_DISPATCHER)) { Dispatchers.Default }
                        factory(named(IO_DISPATCHER)) { Dispatchers.IO }
                    }
                    
                    //get(qualifier = named(IO_DISPATCHER)
                    """.trimIndent()
            )

            //https://github.com/Egi10/sainggik
            KotlinCode(
                code = """
                    //dispatchers interface
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO

                    interface DispatcherProvider {
                        val io: CoroutineDispatcher
                        val main: CoroutineDispatcher
                        val default: CoroutineDispatcher
                        val unconfined: CoroutineDispatcher
                    }

                    class DefaultDispatcherProvider : DispatcherProvider {
                        override val io: CoroutineDispatcher = Dispatchers.IO
                        override val main: CoroutineDispatcher = Dispatchers.Main
                        override val default: CoroutineDispatcher = Dispatchers.Default
                        override val unconfined: CoroutineDispatcher = Dispatchers.Unconfined
                    }
                    
                    //di
                    import org.koin.dsl.module

                    val dispatcherModule = module {
                        single<DispatcherProvider> { DefaultDispatcherProvider() }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Qualifiers.kt
                    import org.koin.core.annotation.Qualifier

                    @Qualifier
                    annotation class WorkerThread

                    @Qualifier
                    annotation class WorkerIOThread
                    
                    //CoroutineModule.kt
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO
                    import org.koin.core.annotation.Configuration
                    import org.koin.core.annotation.Module
                    import org.koin.core.annotation.Single

                    @Module
                    @Configuration
                    class CoroutineModule {
                        @WorkerThread
                        @Single
                        fun provideCoroutineDispatcher(): CoroutineDispatcher = Dispatchers.Default

                        @WorkerIOThread
                        @Single
                        fun provideCoroutineIODispatcher(): CoroutineDispatcher = Dispatchers.IO
                    }
                    
                    //@Module(includes = [CoroutineModule::class])
                    """.trimIndent()
            )

            //https://github.com/Shayne3000/Donezo
            KotlinCode(
                code = """
                    import kotlinx.coroutines.*
                    import org.koin.core.qualifier.named
                    import org.koin.dsl.module

                    const val DISPATCHER_MAIN = "dispatcher-main"
                    const val DISPATCHER_IO = "dispatcher-io"
                    const val DISPATCHER_DEFAULT = "dispatcher-default"
                    const val DISPATCHER_UNCONFINED = "dispatcher-unconfined"

                    /*
                    //Only CommonMain
                    val dispatcherModule =
                        module {
                            single<CoroutineDispatcher>(qualifier = named("io")) { Dispatchers.IO }
                    }

                    //access
                    get(qualifier = named("IO")

                    fun getRoomDatabase(
                        coroutineDispatcher: CoroutineDispatcher
                    ) {}
                    */
                    
                    val dispatcherModule = module {
                        single(named(DISPATCHER_MAIN)) {
                            Dispatchers.Main
                        }
                        single(named(DISPATCHER_IO)) {
                            Dispatchers.IO
                        }
                        single(named(DISPATCHER_DEFAULT)) {
                            Dispatchers.Default
                        }
                        single(named(DISPATCHER_UNCONFINED)) {
                            Dispatchers.Unconfined
                        }
                    }

                    object CoreApplication {
                        fun initialize(appDeclaration: KoinAppDeclaration = {}) = startKoin {
                            appDeclaration()
                            modules(
                                listOf(
                                    dispatcherModule
                                )
                            )
                        }
                    }

                    //Access
                    val dataSourceModule = module {
                        single<MealRemoteDataSource> {
                            MealRemoteDataSourceImpl(
                                httpClient = get(),
                                dispatcher = get(named(DISPATCHER_IO))
                            )
                        }
                    }

                    class MealRemoteDataSourceImpl(
                        private val httpClient: HttpClient,
                        private val dispatcher: CoroutineDispatcher) : MealRemoteDataSource
                    {
                        suspend fun getMealsByArea(areaName: String): List<FilteredMealItemResponse> =
                        withContext(dispatcher) {
                            val response = httpClient.get("filter.php") {
                                url {
                                        parameters.append("a", areaName)
                                }
                            }.body<FilteredMealResponse>()

                        return@withContext response.meals
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //sealed dispatchers
                    
                    val commonModule = module {
                        single<AppDispatchers> { AppDispatchers }
                    }
                    
                    /**
                     * Provides coroutine dispatchers for different execution contexts.
                     *
                     * This sealed interface allows for dependency injection while providing
                     * a default implementation via companion object for production use.
                     * Tests can provide custom implementations for controllable test dispatchers.
                     */
                    sealed interface AppDispatchers {
                        val io: CoroutineDispatcher
                        val main: CoroutineDispatcher
                        val default: CoroutineDispatcher

                        companion object : AppDispatchers {
                            override val io: CoroutineDispatcher = Dispatchers.IO
                            override val main: CoroutineDispatcher = Dispatchers.Main
                            override val default: CoroutineDispatcher = Dispatchers.Default
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "KMP Icon Generate",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    plugins {
                        id("io.github.qamarelsafadi.kmp.app.icon.generator") version "1.2.6"

                        //CMD
                        ./gradlew generateIcons
                    }

                    """.trimIndent()
            )

            //https://github.com/matt1007k/Cine-CMP.git
            SpanText(
                text = "Map iOS",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier

                    @Composable
                    expect fun NativeButton(
                        label: String,
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier
                    )
                    
                    //iosMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.viewinterop.UIKitViewController
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.useContents
                    import platform.UIKit.UIScreen
                    
                    @OptIn(ExperimentalForeignApi::class)
                    @Composable
                    actual fun NativeButton(label: String, onClick: () -> Unit, modifier: Modifier) {
                        val factory = LocalNativeViewFactory.current
                        val screenSize = UIScreen.mainScreen.bounds.useContents { size }

                        UIKitViewController(
                            modifier = modifier,
                            factory = {
                                factory.createButtonView(
                                    label = label, onClick = onClick
                                )
                            }
                        )
                    }
                    
                    import platform.UIKit.UIViewController

                    interface NativeViewFactory {
                        fun createButtonView(
                            label: String,
                            onClick: () -> Unit
                        ): UIViewController
                    }
                    
                    import androidx.compose.foundation.layout.Column
                    import androidx.compose.foundation.layout.WindowInsets
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.foundation.layout.systemBars
                    import androidx.compose.foundation.layout.windowInsetsPadding
                    import androidx.compose.material3.Text
                    import androidx.compose.runtime.CompositionLocalProvider
                    import androidx.compose.runtime.staticCompositionLocalOf
                    import androidx.compose.ui.Alignment
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.viewinterop.UIKitViewController
                    import androidx.compose.ui.window.ComposeUIViewController
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.UIKit.UIViewController

                    val LocalNativeViewFactory = staticCompositionLocalOf<NativeViewFactory> {
                        error("No view factory provided.")
                    }

                    fun MainViewController(
                        nativeViewFactory: NativeViewFactory
                    ) = ComposeUIViewController(
                        configure = { initKoin() }
                    ) {
                        CompositionLocalProvider(LocalNativeViewFactory provides nativeViewFactory) {
                            App()
                        }
                    }
                    
                    //iosApp
                    
                    //IOSButton.swift
                    import SwiftUI
                    import ComposeApp
                    import MapKit

                    class IOSButtonFactory: NativeViewFactory {
                        static var shared = IOSButtonFactory()
                        func createButtonView(label: String, onClick: @escaping () -> Void) -> UIViewController {
                            let view = AnnotatedMapView()
                               
                            return UIHostingController(rootView: view)
                        }
                    }

                    #Preview {
                        IOSButton(label: "Hello", action: {})
                    }

                    struct AnnotatedMapView: View {
                        // Manages map region state
                        @State private var region = MKCoordinateRegion(
                            center: CLLocationCoordinate2D(latitude: 51.5074, longitude: -0.1278),
                            span: MKCoordinateSpan(latitudeDelta: 0.1, longitudeDelta: 0.1)
                        )
                        // Displays a map with a custom annotation
                        var body: some View {
                            Map(coordinateRegion: $region, annotationItems: [Landmark.example]) { landmark in
                                MapMarker(coordinate: landmark.coordinate, tint: .blue)
                            }
                           //.frame(width: 300, height: 300)
                        }
                    }

                    struct Landmark: Identifiable {
                        let id = UUID()
                        let name: String
                        let coordinate: CLLocationCoordinate2D

                        static let example = Landmark(
                            name: "Big Ben",
                            coordinate: CLLocationCoordinate2D(latitude: 51.5007, longitude: -0.1246)
                        )
                    }
                    
                    //ComposeView.swift
                    import UIKit
                    import SwiftUI
                    import ComposeApp

                    struct ComposeView: UIViewControllerRepresentable {
                        func makeUIViewController(context: Context) -> UIViewController {
                            MainViewControllerKt.MainViewController(nativeViewFactory: IOSButtonFactory.shared)
                        }

                        func updateUIViewController(_ uiViewController: UIViewController, context: Context) {}
                    }

                    struct ContentView: View {
                        var body: some View {
                            ComposeView()
                                .ignoresSafeArea(.all)
                                    .ignoresSafeArea(.keyboard) // Compose has own keyboard handler
                        }
                    }
                    
                    //iOSApp.kt
                    @main
                    struct iOSApp: App {
                        var body: some Scene {
                            WindowGroup {
                                ContentView()
                                    .ignoresSafeArea()
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import coil3.ImageLoader
                    import coil3.PlatformContext
                    import coil3.disk.DiskCache
                    import coil3.memory.MemoryCache
                    import coil3.request.crossfade
                    import coil3.util.DebugLogger
                    import okio.FileSystem

                    //  Coil ImageLoader configuration   
                    //1
                    fun getImageLoader(context: PlatformContext): ImageLoader {
                        return ImageLoader.Builder(context)
                            .memoryCache {
                                MemoryCache.Builder().maxSizePercent(context, percent = 0.25).build()
                            }
                            .diskCache {
                                newDiskCache()
                            }
                            .apply {
                                logger(DebugLogger())
                            }
                            .crossfade(true)
                            .build()
                    }
                    
                    //2
                    setSingletonImageLoaderFactory { context: PlatformContext ->
                        ImageLoader.Builder(context)
                            .memoryCachePolicy(CachePolicy.ENABLED)
                            .memoryCache {
                                MemoryCache.Builder()
                                .maxSizePercent(context, percent = 0.25)
                                .build()
                            }
                            .diskCachePolicy(CachePolicy.ENABLED)
                            .diskCache {
                                DiskCache.Builder()
                                .directory(FileSystem.SYSTEM_TEMPORARY_DIRECTORY / "image_cache")
                                .maxSizePercent(percent = 0.02)
                                .build()
                            }
                            .crossfade(true)
                            .apply {
                                if (isDebugBuild()) {
                                    logger(DebugLogger())
                                }
                            }
                        .build()
                    }

                    private fun newDiskCache(): DiskCache {
                        return DiskCache.Builder()
                            .directory(FileSystem.SYSTEM_TEMPORARY_DIRECTORY / "image_cache")
                            .maxSizeBytes(512L * 1024 * 1034) //512MB
                            .build()
                    }

                    import androidx.compose.runtime.Composable
                    import coil3.annotation.ExperimentalCoilApi
                    import coil3.compose.setSingletonImageLoaderFactory
                    import io.github.rubenquadros.timetowish.core.imageLoader.getImageLoader
                    import io.github.rubenquadros.timetowish.ui.TWTheme
                    import org.koin.compose.KoinApplication

                    @OptIn(ExperimentalCoilApi::class)
                    @Composable
                    fun TWApp() {
                        setSingletonImageLoaderFactory { platformContext ->
                            getImageLoader(platformContext)
                        }


                        KoinApplication(
                            application = koinConfig().config
                        ) {
                            TWTheme {

                            }
                        }
                    }

                    import org.koin.dsl.KoinConfiguration
                    import org.koin.dsl.includes
                    import org.koin.dsl.koinConfiguration
                    import org.koin.ksp.generated.module

                    expect fun koinAppConfig(): KoinConfiguration

                    internal fun koinConfig() = koinConfiguration {
                        includes(koinAppConfig())
                        //All modules go here
                        modules(CommonModule().module, FeatureModule().module, ServiceModule().module)
                    }

                    //iosMain
                    import org.koin.dsl.KoinConfiguration
                    import org.koin.dsl.koinConfiguration

                    actual fun koinAppConfig(): KoinConfiguration = koinConfiguration {
                        printLogger()
                    }

                    //androidMain
                    import org.koin.android.ext.koin.androidContext
                    import org.koin.android.ext.koin.androidLogger
                    import org.koin.dsl.KoinConfiguration
                    import org.koin.dsl.koinConfiguration

                    actual fun koinAppConfig(): KoinConfiguration = koinConfiguration {
                        androidLogger()
                        androidContext(MainApplication.instance ?: error("Application is not initialized"))
                    }

                    import android.app.Application
                    class MainApplication : Application() {
                        override fun onCreate() {
                            super.onCreate()

                            instance = this
                        }

                        companion object {
                            var instance: Application? = null
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Grippo-app/grippo-mobile.git
            KotlinCode(
                code = """
                    @Composable
                    internal fun App() {
                        setSingletonImageLoaderFactory { context ->
                            ImageLoader.Builder(context)
                                .components {
                                    add(KtorNetworkFetcherFactory())
                                }
                                .logger(DebugLogger())
                                .build()
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Grippo-app/grippo-mobile.git
            KotlinCode(
                code = """
                    public object Koin {
                        public fun init(
                            appDeclaration: KoinAppDeclaration = {},
                        ): KoinApplication = KoinPlatformTools.defaultContext().startKoin {
                            appDeclaration()
                            modules(
                                ImageLoaderModule().module
                            )
                        }
                    }
                    
                    @Module(includes = [HttpModule::class])
                    @ComponentScan
                    public class ImageLoaderModule {

                        @Single
                        internal fun imageLoaderFactory(
                            httpClient: HttpClient,
                        ): SingletonImageLoader.Factory {
                            return SingletonImageLoader.Factory { context ->
                                ImageLoader.Builder(context)
                                    .crossfade(true)
                                    .maxBitmapSize(Size(400,400))
                                    .components { add(KtorNetworkFetcherFactory(httpClient)) }
                                    .build()
                            }
                        }

                        @Single(createdAtStart = true)
                        internal fun imageLoaderInitializer(factory: SingletonImageLoader.Factory): ImageLoaderInitializer {
                            return ImageLoaderInitializer(factory)
                        }
                    }
                    
                    internal class ImageLoaderInitializer(factory: SingletonImageLoader.Factory) {
                        init {
                            SingletonImageLoader.setSafe(factory)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Chart Library Links",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    https://www.patrykandpatrick.com/vico/guide/stable/getting-started
                    https://github.com/patrykandpatrick/vico
                    https://github.com/filin2hat/BikeCalcMP
                    https://github.com/hi-manshu/charty
                    https://github.com/TheChance101/AAY-chart
                    https://github.com/KoalaPlot/koalaplot-core
                    https://github.com/hossein-no1/compose-multiplatform-linechart
                    https://github.com/rootachieve/Koraph
                    https://github.com/kotalsumit/compose-super-charts
                    https://github.com/AndroidPoet/Drafter //Good
                    https://github.com/Kyriakos-Georgiopoulos/Grafima
                    """.trimIndent()
            )

            SpanText(
                text = "vico chart",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    vico = "2.1.3"
                    vico-multiplatform = { group = "com.patrykandpatrick.vico", name = "multiplatform", version.ref = "vico" }
                    implementation(libs.vico.multiplatform)
                    
                    import com.patrykandpatrick.vico.multiplatform.common.Fill

                    @Composable
                    @Preview
                    fun App() {
                        MaterialTheme {
                            val modelProducer = remember { CartesianChartModelProducer() }

                            LaunchedEffect(Unit) {
                                modelProducer.runTransaction {
                                    columnSeries { series(4, 15, 5, 8, 10, 15, 9, 10, 7, 9, 10, 12, 2, 9, 5, 14) }
                                    lineSeries { series(1, 5, 4, 7, 3, 14, 5, 9, 9, 14, 7, 13, 14, 4, 10, 12) }
                                }
                            }

                            CartesianChartHost(
                                rememberCartesianChart(
                                    rememberColumnCartesianLayer(
                                        ColumnCartesianLayer.ColumnProvider.series(
                                            rememberLineComponent(fill = Fill(Color(0xffffc002)), thickness = 16.dp)
                                        )
                                    ),
                                    rememberLineCartesianLayer(
                                        LineCartesianLayer.LineProvider.series(
                                            LineCartesianLayer.Line(LineCartesianLayer.LineFill.single(Fill(Color(0xffee2b2b))))
                                        )
                                    ),
                                    startAxis = VerticalAxis.rememberStart(),
                                    bottomAxis = HorizontalAxis.rememberBottom(),
                                ),
                                modelProducer
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Compose Chart",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/tamtom/StepsShare-oss
            KotlinCode(
                code = $$"""
                    //Column Chart
                    @Composable
                    fun TrendingChartDisplay(
                        chartData: List<Bars>,
                        selectedDataType: ChartDataType,
                        range: TrendingDateRange
                    ) {
                        val formattingUseCase = com.itdeveapps.stepsshare.domain.usecase.FormattingUseCase()
                        val thickness = when(range) {
                            TrendingDateRange.SEVEN_DAYS -> 14.dp
                            TrendingDateRange.THIRTY_DAYS -> 4.dp
                        }
                        ColumnChart(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(200.dp)
                               ,
                            data = chartData,
                            barProperties = BarProperties(
                                cornerRadius = Bars.Data.Radius.Rectangle(topRight = 4.dp, topLeft = 4.dp),
                                spacing = 2.dp,
                                thickness = thickness
                            ),
                            labelProperties = LabelProperties(
                                textStyle = MaterialTheme.typography.bodySmall.copy(
                                    color = AppColors.onSurface
                                ),
                                enabled = false
                            ),
                            labelHelperProperties = LabelHelperProperties(
                                enabled = true,
                                textStyle = MaterialTheme.typography.bodySmall.copy(
                                    color = AppColors.onSurface
                                )
                            ),
                            indicatorProperties = HorizontalIndicatorProperties(
                                enabled = false,
                                textStyle = MaterialTheme.typography.bodySmall.copy(
                                    color = AppColors.onSurface
                                ),
                                count = IndicatorCount.CountBased(count = 5),
                                contentBuilder = { indicator ->
                                    when (selectedDataType) {
                                        ChartDataType.STEPS -> "${indicator.toInt()}"
                                        ChartDataType.CALORIES -> "${indicator.toInt()}"
                                        ChartDataType.DURATION -> {
                                            val (formattedTime, unit) = formattingUseCase.formatTime(indicator)
                                            "$formattedTime $unit"
                                        }
                                        ChartDataType.DISTANCE -> {
                                            val (formattedDistance, unit) = formattingUseCase.formatDistance(indicator)
                                            "$formattedDistance $unit"
                                        }
                                    }
                                }
                            ),
                            gridProperties = GridProperties(
                                enabled = true,
                                xAxisProperties = GridProperties.AxisProperties(enabled = false),
                                yAxisProperties = GridProperties.AxisProperties(
                                    enabled = true,
                                    color = SolidColor(AppColors.onSurface.copy(alpha = 0.3f)),
                                    thickness = 1.dp,
                                    lineCount = 1
                                )
                            ),
                            dividerProperties = DividerProperties(
                                xAxisProperties = LineProperties(
                                    thickness = 0.2.dp,
                                    color = SolidColor(Color.Gray.copy(alpha = 0.5f)),
                                    style = StrokeStyle.Dashed(intervals = floatArrayOf(15f, 15f), phase = 10f)
                                ),
                                yAxisProperties = LineProperties(
                                    thickness = 0.2.dp,
                                    color = SolidColor(Color.Gray.copy(alpha = 0.5f)),
                                    style = StrokeStyle.Dashed(intervals = floatArrayOf(15f, 15f), phase = 10f)
                                )
                            ),
                            animationMode = AnimationMode.Together(),
                            animationDelay = 0,
                            animationSpec = snap()
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Create Multi Module",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //1. Create package core-network
                    //2. Crate build.gradle.kts
                    
                    //Add the code build.gradle.kts
                    import org.jetbrains.kotlin.gradle.ExperimentalKotlinGradlePluginApi
                    import org.jetbrains.kotlin.gradle.dsl.JvmTarget

                    plugins {
                        alias(libs.plugins.kotlinMultiplatform)
                        alias(libs.plugins.androidLibrary)
                        alias(libs.plugins.kotlinx.serialization)
                    }

                    kotlin {
                        androidTarget {
                            @OptIn(ExperimentalKotlinGradlePluginApi::class)
                            compilerOptions {
                                jvmTarget.set(JvmTarget.JVM_11)
                            }
                        }

                        iosX64()
                        iosArm64()
                        iosSimulatorArm64()

                        targets.withType<org.jetbrains.kotlin.gradle.plugin.mpp.KotlinNativeTarget> {
                            binaries.all {
                                freeCompilerArgs += "-Xdisable-phases=TestRunner" // disable tests
                            }
                        }

                        sourceSets {

                            androidMain.dependencies {
                                implementation(libs.ktor.client.android)
                            }

                            commonMain.dependencies {
                                implementation(projects.feature.details.domain)

                                implementation(libs.kotlinx.serialization)
                                implementation(libs.ktor.client.core)
                                implementation(libs.ktor.client.content.negotiation)
                                implementation(libs.ktor.client.serialization)

                                implementation(libs.koin.core)

                            }
                            commonTest.dependencies {
                                implementation(libs.kotlin.test)
                            }
                            iosMain.dependencies {
                                implementation(libs.ktor.client.ios)
                            }
                        }
                    }

                    android {
                        namespace = "dev.himanshu.moviesearchapp.coreNetwork"
                        compileSdk = libs.versions.android.compileSdk.get().toInt()
                        compileOptions {
                            sourceCompatibility = JavaVersion.VERSION_11
                            targetCompatibility = JavaVersion.VERSION_11
                        }
                        defaultConfig {
                            minSdk = libs.versions.android.minSdk.get().toInt()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Platform Specific ArrowBack
                    
                    import androidx.compose.material.icons.Icons
                    import androidx.compose.material.icons.automirrored.rounded.ArrowBack
                    import androidx.compose.material.icons.automirrored.rounded.ArrowBackIos
                    import androidx.compose.ui.graphics.vector.ImageVector

                    object TwineIcons

                    val TwineIcons.ArrowBack: ImageVector
                      get() {
                        return if (platform is Platform.Apple) {
                          Icons.AutoMirrored.Rounded.ArrowBackIos
                        } else {
                          Icons.AutoMirrored.Rounded.ArrowBack
                        }
                      }
                    """.trimIndent()
            )

            SpanText(
                text = "Code",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/uangkuid/OrataDesign-Web.git
            KotlinCode(
                code = """
                    design = "0.0.1-Alpha1"
                    designsystem = { module = "com.oratakashi:design", version.ref = "design" }
                    
                    implementation("dev.snipme:highlights:1.1.0")
                    
                    compose.desktop {
                        application {
                            mainClass = "com.oratakashi.design.docs.MainKt"

                            nativeDistributions {
                                targetFormats(TargetFormat.Dmg, TargetFormat.Msi, TargetFormat.Deb)
                                packageName = "com.oratakashi.design.docs"
                                packageVersion = "1.0.0"
                            }
                        }
                    }
                    
                    import dev.snipme.highlights.Highlights
                    import dev.snipme.highlights.model.SyntaxLanguage
                    import dev.snipme.highlights.model.SyntaxThemes

                    @Composable
                    fun Code(
                        fileName: String = "",
                        code: String = "",
                        language: SyntaxLanguage = SyntaxLanguage.DEFAULT,
                        canExpand: Boolean = true
                    ) {
                        val highlights = remember(code, language) {
                            Highlights
                                .Builder(
                                    code = code.trimIndent(),
                                    theme = SyntaxThemes.pastel(darkMode = true),
                                    language = language
                                )
                                .build()
                        }
                        var expanded by remember { mutableStateOf(false) }
                        val minCollapsedHeight = 120.dp

                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .animateContentSize(
                                    animationSpec = spring(
                                        dampingRatio = Spring.DampingRatioMediumBouncy,
                                        stiffness = Spring.StiffnessLow
                                    )
                                ),
                            shape = RoundedCornerShape(16.dp),
                        ) {
                            Column {
                                // Header
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(
                                            horizontal = 16.dp,
                                            vertical = 8.dp
                                        ),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    AnimatedVisibility(fileName.isNotBlank()) {
                                        Text(
                                            text = fileName,
                                            style = OrataTheme.typography.bodyMedium(),
                                            color = OrataTheme.colors.onSurface
                                        )
                                    }

                                    OraTransparentButton(
                                        onClick = {

                                        },
                                        size = OraButtonSize.XSmall,
                                        label = "Copy",
                                        iconLeft = {
                                            Icon(imageVector = FeatherIcons.Copy, null)
                                        }
                                    )
                                }

                                HorizontalDivider(
                                    thickness = 1.dp,
                                    color = Color(0xFF3F3F46)
                                )

                                Box(
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    CodeTextView(
                                        highlights = highlights,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .then(
                                                if (canExpand && !expanded) {
                                                    Modifier.heightIn(max = minCollapsedHeight)
                                                } else {
                                                    Modifier.wrapContentHeight()
                                                }
                                            )
                                            .padding(16.dp)
                                    )

                                    // Gradient Overlay saat collapsed
                                    if (code.isNotBlank() && canExpand && !expanded) {
                                        Box(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .height(80.dp)
                                                .align(Alignment.BottomCenter)
                                                .background(
                                                    brush = Brush.verticalGradient(
                                                        colors = listOf(
                                                            Color.Transparent,
                                                            OrataTheme.colors.surfaceContainerHigh
                                                        )
                                                    )
                                                )
                                        )
                                    }
                                }

                                // Show/Hide Button
                                if (code.isNotBlank() && canExpand) {

                                    AnimatedVisibility(expanded) {
                                        HorizontalDivider(
                                            modifier = Modifier
                                                .padding(top = 8.dp)
                                        )
                                    }

                                    OraTransparentButton(
                                        onClick = { expanded = !expanded },
                                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                                        label = if (expanded) "Hide code" else "Show code",
                                        size = OraButtonSize.Small
                                    )
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun CodeTextView(
                        modifier: Modifier = Modifier.background(Color.Transparent),
                        highlights: Highlights
                    ) {
                        var textState by remember {
                            mutableStateOf(AnnotatedString(highlights.getCode()))
                        }

                        LaunchedEffect(highlights) {
                            textState = highlights
                                .getHighlights()
                                .generateAnnotatedString(highlights.getCode())
                        }

                        SelectionContainer {
                            Text(
                                modifier = modifier,
                                text = textState
                            )
                        }
                    }
                    
                    internal const val TAB_LENGTH = 4
                    internal const val TAB_CHAR = "\t"
                    
                    fun List<CodeHighlight>.generateAnnotatedString(code: String) =
                        buildAnnotatedString {
                            append(code)

                            forEach {
                                when (it) {
                                    is BoldHighlight -> addStyle(
                                        SpanStyle(fontWeight = FontWeight.Bold),
                                        start = it.location.start,
                                        end = it.location.end,
                                    )

                                    is ColorHighlight -> addStyle(
                                        SpanStyle(color = Color(it.rgb).copy(alpha = 1f)),
                                        start = it.location.start,
                                        end = it.location.end,
                                    )
                                }
                            }
                        }
                    
                    internal fun TextFieldValue.updateIndentations(handleIndentations: Boolean) =
                        if (handleIndentations && text.contains(TAB_CHAR)) {
                            val result = text.replace(TAB_CHAR, " ".repeat(TAB_LENGTH))
                            this.copy(text = result, TextRange(selection.start + TAB_LENGTH - 1))
                        } else {
                            this
                        }
                    

                    internal fun TextFieldValue.copySpanStyles(source: TextFieldValue) =
                        this.copy(
                            annotatedString = buildAnnotatedString {
                                append(text)
                                source.annotatedString.spanStyles.forEach {
                                    if (it.start >= 0 && it.end > it.start && it.end < text.length) {
                                        addStyle(it.item, it.start, it.end)
                                    }
                                }
                            }
                        )
                    """.trimIndent()
            )

            SpanText(
                text = "Liquid",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3Api::class, ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun AppTopBar(
                        modifier: Modifier = Modifier,
                        title: String,
                        navigationIcon: ImageVector = Hicon.ArrowLeft,
                        onNavigationClick: (() -> Unit)? = null,
                        windowInsets: WindowInsets = TopAppBarDefaults.windowInsets,
                        actions: @Composable () -> Unit = {},
                    ) {
                        val liquidTopBarState = rememberLiquidState()
                        TopAppBar(
                            title = {
                                Text(
                                    text = title,
                                    style = MaterialTheme.typography.titleLarge
                                )
                            },
                            modifier = modifier.then(
                                if (isLiquidEnabled()) {
                                    Modifier.liquid(liquidState = liquidTopBarState) {
                                        this.shape = CutCornerShape(0.dp)
                                        this.frost = 8.dp
                                        this.curve = .4f
                                        this.refraction = .1f
                                        this.dispersion = .2f
                                    }
                                } else Modifier
                            ),
                            navigationIcon = {
                                if (onNavigationClick != null) {
                                    IconButton(
                                        shapes = IconButtonDefaults.shapes(),
                                        onClick = onNavigationClick
                                    ) {
                                        Icon(
                                            imageVector = navigationIcon,
                                            contentDescription = "Back"
                                        )
                                    }
                                }
                            },
                            windowInsets = windowInsets,
                            actions = { actions() },
                            colors = TopAppBarDefaults.topAppBarColors(
                                containerColor = Color.Transparent,
                                scrolledContainerColor = Color.Transparent
                            )
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Permissions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/codernewbie04/Mapbox-CMP
            KotlinCode(
                code = """
                    @Composable
                    fun App() {
                        MaterialTheme {
                            Scaffold { innerPadding ->
                                PermissionsRequester(
                                    permissions = listOf(
                                        PermissionRequest(
                                            permission = Permission.LOCATION,
                                            title = "Location",
                                            description = "To show user location",
                                            icon = Icons.Default.LocationOn
                                        )
                                    ),
                                    onAllPermissionsGranted = {
                                        println("All permissions granted!")
                                    }
                                ) { controller ->
                                     val locationTracker = remember(controller) {
                   
                                     }
                                }
                            }
                        }
                    }
                    
                    import com.akmalmf.cmpmapbox.model.PermissionRequest
                    import dev.icerock.moko.permissions.DeniedAlwaysException
                    import dev.icerock.moko.permissions.Permission
                    import dev.icerock.moko.permissions.PermissionState
                    import dev.icerock.moko.permissions.PermissionsController
                    import dev.icerock.moko.permissions.compose.BindEffect
                    import dev.icerock.moko.permissions.compose.PermissionsControllerFactory
                    import dev.icerock.moko.permissions.compose.rememberPermissionsControllerFactory
                    import kotlinx.coroutines.launch

                    @Composable
                    fun PermissionsRequester(
                        permissions: List<PermissionRequest>,
                        onAllPermissionsGranted: () -> Unit = {},
                        content: @Composable (controller: PermissionsController) -> Unit
                    ) {
                        val factory: PermissionsControllerFactory = rememberPermissionsControllerFactory()
                        val controller = remember(factory) { factory.createPermissionsController() }
                        val scope = rememberCoroutineScope()

                        BindEffect(controller)

                        var permissionStates by remember {
                            mutableStateOf(permissions.associate { it.permission to PermissionState.NotDetermined })
                        }

                        var permissionDeniedAlways by rememberSaveable { mutableStateOf(false) }

                        LaunchedEffect(Unit) {
                            permissionStates = permissions.associate { req ->
                                req.permission to controller.getPermissionState(req.permission)
                            }
                        }

                        val allGranted = permissionStates.values.all { it == PermissionState.Granted }
                        val anyPermanentlyDenied = permissionStates.values.any { it == PermissionState.DeniedAlways }

                        when {
                            allGranted -> {
                                LaunchedEffect(Unit) {
                                    onAllPermissionsGranted()
                                }
                                content(controller)
                            }

                            anyPermanentlyDenied || permissionDeniedAlways -> {
                                PermissionsDeniedScreen(
                                    permissions = permissions,
                                    permissionStates = permissionStates,
                                    onOpenSettings = {
                                        controller.openAppSettings()
                                    }
                                )
                            }

                            else -> {
                                PermissionsRequestScreen(
                                    permissions = permissions,
                                    permissionStates = permissionStates,
                                    onRequestPermissions = {
                                        scope.launch {
                                            permissions.forEach { req ->
                                                if (permissionStates[req.permission] != PermissionState.Granted) {
                                                    try {
                                                        controller.providePermission(req.permission)
                                                    } catch (e: DeniedAlwaysException) {
                                                        permissionDeniedAlways = true
                                                    } catch (e: Exception) {
                                                        e.printStackTrace()
                                                    }
                                                }
                                            }
                                            permissionStates = permissions.associate { req ->
                                                req.permission to controller.getPermissionState(req.permission)
                                            }
                                        }
                                    }
                                )
                            }
                        }
                    }

                    @Composable
                    private fun PermissionsRequestScreen(
                        permissions: List<PermissionRequest>,
                        permissionStates: Map<Permission, PermissionState>,
                        onRequestPermissions: () -> Unit
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(color = Color.White)
                                .padding(24.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Card(
                                modifier = Modifier.fillMaxWidth(),
                                elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
                            ) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(24.dp)
                                        .verticalScroll(rememberScrollState()),
                                    horizontalAlignment = Alignment.CenterHorizontally,
                                    verticalArrangement = Arrangement.spacedBy(16.dp)
                                ) {
                                    Icon(
                                        imageVector = Icons.Default.Security,
                                        contentDescription = null,
                                        modifier = Modifier.size(80.dp),
                                        tint = MaterialTheme.colorScheme.primary
                                    )

                                    Text(
                                        text = "Permission Required",
                                        style = MaterialTheme.typography.headlineSmall,
                                        fontWeight = FontWeight.Bold,
                                        textAlign = TextAlign.Center
                                    )

                                    Text(
                                        text = "The app needs a few permissions to work properly.",
                                        style = MaterialTheme.typography.bodyMedium,
                                        textAlign = TextAlign.Center,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )

                                    Spacer(modifier = Modifier.height(8.dp))

                                    permissions.forEach { req ->
                                        PermissionItem(
                                            title = req.title,
                                            description = req.description,
                                            icon = req.icon,
                                            isGranted = permissionStates[req.permission] == PermissionState.Granted
                                        )
                                    }

                                    Spacer(modifier = Modifier.height(8.dp))

                                    Button(
                                        onClick = onRequestPermissions,
                                        modifier = Modifier.fillMaxWidth()
                                    ) {
                                        Icon(Icons.Default.Check, contentDescription = null)
                                        Spacer(modifier = Modifier.width(8.dp))
                                        Text("Allow All Access")
                                    }
                                }
                            }
                        }
                    }
                    
                    @Composable
                    private fun PermissionsDeniedScreen(
                        permissions: List<PermissionRequest>,
                        permissionStates: Map<Permission, PermissionState>,
                        onOpenSettings: () -> Unit
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(24.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Card(
                                modifier = Modifier.fillMaxWidth(),
                                elevation = CardDefaults.cardElevation(defaultElevation = 4.dp),
                                colors = CardDefaults.cardColors(
                                    containerColor = MaterialTheme.colorScheme.errorContainer
                                )
                            ) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(24.dp)
                                        .verticalScroll(rememberScrollState()),
                                    horizontalAlignment = Alignment.CenterHorizontally,
                                    verticalArrangement = Arrangement.spacedBy(16.dp)
                                ) {
                                    Icon(
                                        imageVector = Icons.Default.Warning,
                                        contentDescription = null,
                                        modifier = Modifier.size(80.dp),
                                        tint = MaterialTheme.colorScheme.error
                                    )

                                    Text(
                                        text = "Access Denied",
                                        style = MaterialTheme.typography.headlineSmall,
                                        fontWeight = FontWeight.Bold,
                                        textAlign = TextAlign.Center,
                                        color = MaterialTheme.colorScheme.onErrorContainer
                                    )

                                    Text(
                                        text = "Some permissions have been denied. Enable the following permissions in the app settings",
                                        style = MaterialTheme.typography.bodyMedium,
                                        textAlign = TextAlign.Center,
                                        color = MaterialTheme.colorScheme.onErrorContainer
                                    )

                                    Spacer(modifier = Modifier.height(8.dp))

                                    permissions.forEach { req ->
                                        val state = permissionStates[req.permission]
                                        if (state == PermissionState.DeniedAlways || state == PermissionState.Denied) {
                                            PermissionItem(
                                                title = req.title,
                                                description = req.description,
                                                icon = req.icon,
                                                isGranted = false,
                                                isDenied = true
                                            )
                                        }
                                    }

                                    Spacer(modifier = Modifier.height(8.dp))

                                    Button(
                                        onClick = onOpenSettings,
                                        modifier = Modifier.fillMaxWidth(),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = MaterialTheme.colorScheme.error
                                        )
                                    ) {
                                        Icon(Icons.Default.Settings, contentDescription = null)
                                        Spacer(modifier = Modifier.width(8.dp))
                                        Text("Open Setting")
                                    }
                                }
                            }
                        }
                    }

                    @Composable
                    private fun PermissionItem(
                        title: String,
                        description: String,
                        icon: ImageVector,
                        isGranted: Boolean,
                        isDenied: Boolean = false
                    ) {
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            colors = CardDefaults.cardColors(
                                containerColor = when {
                                    isGranted -> MaterialTheme.colorScheme.primaryContainer
                                    isDenied -> MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.5f)
                                    else -> MaterialTheme.colorScheme.surfaceVariant
                                }
                            )
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                horizontalArrangement = Arrangement.spacedBy(12.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    imageVector = icon,
                                    contentDescription = null,
                                    modifier = Modifier.size(32.dp),
                                    tint = if (isGranted) {
                                        MaterialTheme.colorScheme.primary
                                    } else if (isDenied) {
                                        MaterialTheme.colorScheme.error
                                    } else {
                                        MaterialTheme.colorScheme.onSurfaceVariant
                                    }
                                )

                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        text = title,
                                        style = MaterialTheme.typography.titleSmall,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                    Text(
                                        text = description,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }

                                if (isGranted) {
                                    Icon(
                                        imageVector = Icons.Default.CheckCircle,
                                        contentDescription = "Granted",
                                        tint = MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/PhyoThihaWin/fooddi-cmp.git
            KotlinCode(
                code = """
                    //Notification Permission
                    
                    @Composable
                    fun RequestNotificationPermission() {
                        val factory: PermissionsControllerFactory = rememberPermissionsControllerFactory()
                        val controller: PermissionsController = remember(factory) { factory.createPermissionsController() }
                        val coroutineScope: CoroutineScope = rememberCoroutineScope()
                        var isGranted by remember { mutableStateOf<Boolean?>(null) }

                        BindEffect(controller)

                        LaunchedEffect(Unit) {
                            isGranted = controller.isPermissionGranted(Permission.REMOTE_NOTIFICATION)
                        }

                        if (isGranted == false) {
                            PermissionDialog {
                                coroutineScope.launch {
                                    controller.providePermission(Permission.REMOTE_NOTIFICATION)
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun PermissionDialog(onRequestPermission: () -> Unit) {
                        var showWarningDialog by remember { mutableStateOf(true) }

                        if (showWarningDialog) {
                            AlertDialog(
                                modifier = Modifier.alertDialog(),
                                title = { Text(stringResource(Res.string.notification_permission_title)) },
                                text = { Text(stringResource(Res.string.notification_permission_description)) },
                                confirmButton = {
                                    TextButton(
                                        onClick = {
                                            onRequestPermission()
                                            showWarningDialog = false
                                        },
                                        modifier = Modifier.textButton(),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = MaterialTheme.colorScheme.tertiary,
                                            contentColor = MaterialTheme.colorScheme.onTertiary
                                        )
                                    ) { Text(text = stringResource(Res.string.request_notification_permission)) }
                                },
                                onDismissRequest = { }
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Shared Code Access",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/mhmmdvg/playit-kmp.git
            KotlinCode(
                code = """
                    //commonMain
                    class CurrentPlaylistsViewModel(
                        private val playlistsRepositoryImpl: PlaylistsRepositoryImpl
                    ) {
                        private val viewModelScope = CoroutineScope(Dispatchers.Main)

                        private val _currentPlaylists = MutableStateFlow<Resource<CurrentPlaylistsResponse>>(Resource.Success(null))
                        val currentPlaylists: StateFlow<Resource<CurrentPlaylistsResponse>> = _currentPlaylists.asStateFlow()
                        val currentPlaylistsFlow: CommonFlow<Resource<CurrentPlaylistsResponse>> = _currentPlaylists.asCommonFlow()

                        fun getCurrentPlaylists() {
                            viewModelScope.launch {
                                _currentPlaylists.value = Resource.Loading()

                                try {
                                    playlistsRepositoryImpl.getCurrentPlaylists().fold(
                                        onSuccess = {
                                            _currentPlaylists.value = Resource.Success(it)
                                        },
                                        onFailure = {
                                            _currentPlaylists.value = Resource.Error(it.message ?: "Something went wrong")
                                        }
                                    )
                                } catch (e: Exception) {
                                    _currentPlaylists.value = Resource.Error(e.message ?: "Something went wrong")
                                }
                            }
                        }
                    }
                    
                    //FlowExtension.kt
                    fun <T>Flow<T>.asCommonFlow(): CommonFlow<T> = CommonFlow(this)

                    class CommonFlow<T>(private val origin: Flow<T>) : Flow<T> by origin {
                        fun watch(block: (T) -> Unit): FlowCloseable {
                            val job = Job()

                            onEach { block(it) }.launchIn(CoroutineScope(Dispatchers.Main + job))

                            return object : FlowCloseable {
                                override fun close() {
                                    job.cancel()
                                }
                            }
                        }
                    }

                    interface FlowCloseable {
                        fun close()
                    }
                    
                    //Resource.kt
                    sealed class Resource<T>(val data: T? = null, val message: String? = null) {
                        class Success<T>(data: T?) : Resource<T>(data)
                        class Error<T>(message: String, data: T? = null) : Resource<T>(data, message)
                        class Loading<T>(data: T? = null) : Resource<T>(data)
                    }
                    
                    //iosMain
                    import Combine
                    import Shared

                    @MainActor
                    class TracksViewModelWrapper: ObservableObject {
                        private let viewModel: TracksViewModel
                        
                        @Published var severalTracks: Resource<SeveralTracks> = ResourceSuccess(data: nil)
                        @Published var isLoading: Bool = false
                        @Published var errorMessage: String? = nil
                        @Published var severalTracksData: SeveralTracks? = nil
                        
                        private var closeable: FlowCloseable?
                        
                        init(tracksRepository: TracksRepositoryImpl) {
                            self.viewModel = TracksViewModel(tracksRepositoryImpl: tracksRepository)
                            
                            startObserving()
                        }
                        
                        func getSeveralTracks() {
                            viewModel.getSeveralTracks()
                        }
                        
                        private func startObserving() {
                            closeable = viewModel.severalTracksFlow.watch(block: { [weak self] state in
                                DispatchQueue.main.async {
                                    guard let self = self, let state = state else {
                                        return
                                    }
                                    
                                    self.severalTracks = state
                                    self.updateUIState(from: state)
                                }
                            })
                        }
                        
                        private func updateUIState(from resource: Resource<SeveralTracks>) {
                            isLoading = false
                            errorMessage = nil
                            severalTracksData = nil
                            
                            if resource is ResourceLoading {
                                isLoading = true
                            } else if let successResource = resource as? ResourceSuccess<SeveralTracks> {
                                severalTracksData = successResource.data
                            } else if let errorResource = resource as? ResourceError<SeveralTracks> {
                                errorMessage = errorResource.message
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/dev-bilal-azzam/contacts-provider.git
            KotlinCode(
                code = """
                    //Convert List to buildList 
                    
                    interface ContactsProvider {
                        suspend fun getAllContacts(fields: Set<ContactField>): List<Contact>
                    }
                    
                    //iosMain
                    //1
                    buildList { add(Contact()) }
                    
                    //2
                    val contacts = mutableListOf<Contact>()
                    contacts.add(Contact())
                    
                    //androidMain
                    buildList { add(Contact()) }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //iosMain Collection Empty Define
                    
                    listOf()
                    emptyList()
                    listOfNotNull()
                    emptyMap<Any?, Any>()
                    
                    //Dictionary
                    var eventParams: [String: Any] = [:]
                    
                    data: Map<String, Any> //commonMain
                    data.map { it.key as? Any to it.value }.toMap() //iosMain
                    
                    """.trimIndent()
            )

            //4. https://github.com/euri16/cmp-google-login
            KotlinCode(
                code = """
                    //1 callback 
                    //commonMain
                    expect suspend fun signInWithFirebase(email: String, password: String): AppUser
                   
                    //iosMain
                    @OptIn(ExperimentalForeignApi::class)
                    actual suspend fun signInWithFirebase(
                        email: String,
                        password: String
                    ): AppUser = suspendCancellableCoroutine { continuation ->
                            continuation.resume() //result
                             continuation.resumeWithException() //exception
                    }
                    
                    //androidMain
                    actual suspend fun signInWithFirebase(
                        email: String,
                        password: String
                    ): AppUser { 
                        return 
                    }
                    
                    //2 iosMain
                    suspendCancellableCoroutine { continuation ->  continuation.resume() }
                    return@suspendCancellableCoroutine //return null
                    
                    //3 return android/iOS
                    //commonMain
                    expect fun getScreenWidth(): Float
                    
                    //androidMain
                    actual fun getScreenWidth(): Float {
                        return android.content.res.Resources.getSystem().displayMetrics.widthPixels /
                                android.content.res.Resources.getSystem().displayMetrics.density
                    }
                    
                    //iosMain
                    @OptIn(ExperimentalForeignApi::class)
                    actual fun getScreenWidth(): Float {
                        val bounds: CValue<CGRect> = UIScreen.mainScreen.bounds
                        return CGRectGetWidth(bounds).toFloat()
                    }
                    
                    //4 return Unit
                    @Composable
                    internal expect fun GoogleLoginButton(
                        onResponse: (AuthResponse) -> Unit
                    ) { }    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //iosMain ViewModel Access using Koin
                    
                    val conversationViewModel = koinViewModel(ConversationViewModel::class)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Screen size
                    import androidx.compose.runtime.Composable

                    @get:Composable
                    expect val screenSize: ScreenSize

                    data class ScreenSize(
                        val width: Float,
                        val height: Float,
                    )
                    
                    //androidMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.platform.LocalConfiguration

                    actual val screenSize: ScreenSize
                        @Composable
                        get() {
                            val configuration = LocalConfiguration.current
                            return ScreenSize(
                                width = configuration.screenWidthDp.toFloat(),
                                height = configuration.screenHeightDp.toFloat(),
                            )                   
                    }
                       
                    //iosMain     
                    import androidx.compose.runtime.Composable
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.useContents
                    import platform.UIKit.UIScreen

                    @OptIn(ExperimentalForeignApi::class)
                    actual val screenSize: ScreenSize
                        @Composable
                        get() {
                            val (width, height) = UIScreen.mainScreen.bounds.useContents { size.width to size.height }
                            return ScreenSize(
                                width = width.toFloat(),
                                height = height.toFloat(),
                            )   
                    }     
                """.trimIndent()
            )

            SpanText(
                text = "ResourceReader",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    //File Reader
                    
                    expect class ResourceReader {
                        suspend fun readText(path: String): String
                    }
                    
                    //iosMain
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.Foundation.NSBundle
                    import platform.Foundation.NSString
                    import platform.Foundation.NSUTF8StringEncoding
                    import platform.Foundation.stringWithContentsOfFile

                    actual class ResourceReader {
                        @OptIn(ExperimentalForeignApi::class)
                        actual suspend fun readText(path: String): String {
                            val fileName = path.substringAfterLast("/").substringBeforeLast(".")
                            val fileExtension = path.substringAfterLast(".")
                            val directory = path.substringBeforeLast("/")
                            val bundlePath = NSBundle.mainBundle.pathForResource(
                                name = fileName,
                                ofType = fileExtension,
                                inDirectory = directory,
                            ) ?: error("Resource not found: $path")
                            return NSString.stringWithContentsOfFile(
                                bundlePath,
                                encoding = NSUTF8StringEncoding,
                                error = null,
                            ) ?: error("Could not read: $path")
                        }
                    }
                    
                    //androidMain
                    import android.content.Context

                    actual class ResourceReader(private val context: Context) {
                        actual suspend fun readText(path: String): String {
                            return context.assets.open(path).bufferedReader().use { it.readText() }
                        }
                    }
                    
                    //wasmJsMain
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.resumeWithException
                    import kotlin.coroutines.suspendCoroutine
                    import kotlin.js.Promise

                    @JsFun("(url) => fetch(url).then(r => r.text())")
                    private external fun jsFetch(url: JsString): Promise<JsString>

                    actual class ResourceReader {
                        actual suspend fun readText(path: String): String {
                            return try {
                                val promise = jsFetch(path.toJsString())
                                val result: JsString = promise.awaitJs()
                                result.toString()
                            } catch (_: Exception) {
                                "{}"
                            }
                        }
                    }

                    private suspend fun <T : JsAny> Promise<T>.awaitJs(): T = suspendCoroutine { cont ->
                        then(
                            onFulfilled = { value: T ->
                                cont.resume(value)
                                value
                            },
                            onRejected = { error: JsAny ->
                                cont.resumeWithException(Exception(error.toString()))
                                error
                            },
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "SwiftUI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import SwiftUI

                    struct AdaptiveListGridView<Content: View, T: Identifiable>: View {
                    	@Environment(\.horizontalSizeClass) private var horizontalSizeClass
                    	
                    	private let items: [T]
                    	private let gridMinSize: CGFloat
                    	private let spacing: CGFloat
                    	private let content: (T) -> Content
                    	
                    	init(
                    		items: [T],
                    		gridMinSize: CGFloat = 360,
                    		spacing: CGFloat = 16,
                    		@ViewBuilder content: @escaping (T) -> Content
                    	) {
                    		self.items = items
                    		self.gridMinSize = gridMinSize
                    		self.spacing = spacing
                    		self.content = content
                    	}
                    	
                    	private var isGrid: Bool {
                    		horizontalSizeClass == .regular
                    	}
                    	
                    	private var gridSize: [GridItem] {
                    		[GridItem(.adaptive(minimum: gridMinSize), spacing: spacing)]
                    	}
                    	
                    	var body: some View {
                    		ScrollView {
                    			if isGrid {
                    				LazyVGrid(columns: gridSize, spacing: spacing) {
                    					ListView()
                    				}
                    			} else {
                    				LazyVStack(spacing: spacing) {
                    					ListView()
                    				}
                    			}
                    		}
                    		.scrollIndicators(.hidden)
                    	}
                    	
                    	@ViewBuilder
                    	private func ListView() -> some View {
                    		ForEach(items) { item in
                    			content(item)
                    		}
                    	}
                    }
                    """.trimIndent()
            )

            //https://github.com/sunildhiman90/Aegis.git (suspend operator fun invoke())
            KotlinCode(
                code = $$"""
                    //commonMain
                    interface LanguageHelper {
                        /**
                         * This method will update app language code in preferences
                         * @param languageCode is the code of Language we are changing our app Language to
                         */
                        fun setLanguageCode(languageCode: String)

                        /**
                         * This method will switch app language at platform level
                         * @param languageCode is the code of Language we are changing our app Language to
                         */
                        suspend fun changeLanguage(languageCode: String)

                        /**
                         * This method will return app language name by languageCode
                         * @param languageCode is the code of Language
                         */
                        suspend fun getLanguageNameByCode(languageCode: String): String?

                        /**
                         * This method will return app current language
                         */
                        suspend fun getCurrentLanguage(): String
                    }
                    
                    /**
                     * This class provides common helper functions for Multi Language Support
                     */
                    abstract class CommonLanguageHelper(
                        private val appSettingsRepository: AppSettingsRepository,
                        private val getLanguagesUseCase: GetLanguagesUseCase,
                    ) : LanguageHelper {

                        override fun setLanguageCode(languageCode: String) {
                            appSettingsRepository.setLanguageCode(languageCode)
                        }

                        override suspend fun changeLanguage(languageCode: String) {
                            setLanguageCode(languageCode)

                            appSettingsRepository.setLanguageName(
                                getLanguageNameByCode(languageCode) ?: EN
                            )
                        }

                        override suspend fun getLanguageNameByCode(languageCode: String): String? {
                            val languages = getLanguagesUseCase.invoke()
                            val language = languages.firstOrNull { it.code == languageCode }?.name
                            return language
                        }

                        override suspend fun getCurrentLanguage(): String {
                            val languageCode = appSettingsRepository.getLanguageCode()
                            return getLanguageNameByCode(languageCode) ?: EN
                        }
                    }
                    
                    //iosMain
                    /**
                     * iOS-specific implementation of LanguageHelper
                     * Uses NSUserDefaults to set AppleLanguages
                     */
                    class IOSLanguageHelper(
                        appSettingsRepository: AppSettingsRepository,
                        getLanguagesUseCase: GetLanguagesUseCase,
                    ) : CommonLanguageHelper(appSettingsRepository, getLanguagesUseCase) {

                        override suspend fun changeLanguage(languageCode: String) {
                            println("IOSLanguageHelper changeLanguage: $languageCode")
                            super.changeLanguage(languageCode)

                            val userDefaults = NSUserDefaults.standardUserDefaults
                            userDefaults.setObject(listOf(languageCode), forKey = "AppleLanguages")
                            userDefaults.synchronize()
                        }
                    }
                    
                    actual val platformModule =
                        module {
                            // Language Helper
                            single<LanguageHelper> {
                                IOSLanguageHelper(
                                    appSettingsRepository = get(),
                                    getLanguagesUseCase = get()
                                )
                            }
                        }
                    
                    //androidMain
                    /**
                     * Android-specific implementation of LanguageHelper
                     * Handles system-level locale changes using LocaleManager (API 33+) or AppCompatDelegate
                     */
                    class AndroidLanguageHelper(
                        appSettingsRepository: AppSettingsRepository,
                        getLanguagesUseCase: GetLanguagesUseCase,
                        private val context: Context,
                    ) : CommonLanguageHelper(
                        appSettingsRepository,
                        getLanguagesUseCase,
                    ) {

                        override suspend fun changeLanguage(languageCode: String) {
                            println("AndroidLanguageHelper changeLanguage: $languageCode")
                            super.changeLanguage(languageCode)

                            val locale = Locale(languageCode)
                            Locale.setDefault(locale)

                            val config = context.resources.configuration
                            config.setLocales(LocaleList.forLanguageTags(languageCode))
                            context.createConfigurationContext(config)

                            // Due to per-app language changes
                            if (Build.VERSION.SDK_INT >= Build.VERSION_CODES.TIRAMISU) {
                                context.getSystemService(LocaleManager::class.java).applicationLocales =
                                    LocaleList.forLanguageTags(languageCode)
                            } else {
                                AppCompatDelegate.setApplicationLocales(LocaleListCompat.forLanguageTags(languageCode))
                            }
                        }
                    }
                    
                     actual val platformModule =
                        module {
                            // Language Helper
                            single<LanguageHelper> {
                                IOSLanguageHelper(
                                    appSettingsRepository = get(),
                                    getLanguagesUseCase = get()
                                )
                            }
                        }
                    
                    data class AppLanguageItem(
                        val name: String,
                        val code: String,
                        val flagDrawable: DrawableResource
                    )
                    
                    /**
                     * Use case for retrieving available languages
                     */
                    interface GetLanguagesUseCase {
                        suspend operator fun invoke(): List<AppLanguageItem>
                    }
                    
                    class GetLanguagesUseCaseImpl : GetLanguagesUseCase {
                        override suspend fun invoke(): List<AppLanguageItem> =
                            listOf(
                                AppLanguageItem(
                                    name = getString(Res.string.text_language_english),
                                    code = EN,
                                    flagDrawable = Res.drawable.uk,
                                ),
                                AppLanguageItem(
                                    name = getString(Res.string.text_language_french),
                                    code = FR,
                                    flagDrawable = Res.drawable.france,
                                ),
                                AppLanguageItem(
                                    name = getString(Res.string.text_language_hindi),
                                    code = HI,
                                    flagDrawable = Res.drawable.ic_hindi,
                                ),
                                AppLanguageItem(
                                    name = getString(Res.string.text_language_spanish),
                                    code = SP,
                                    flagDrawable = Res.drawable.spain,
                                ),
                            )
                    }
                    """.trimIndent()
            )
        }
    }
}

//START PROJECT TEMPLATE
//https://www.youtube.com/watch?v=Atvl0l7fm1Y (KMP New Module Project)
//https://github.com/ywegel/kmp-zeiterfassung (Detekt, ktlint)
//https://github.com/kvm-dev/yam
//https://github.com/donald-okara/KleanBoy.git
//https://github.com/VanDoanh2301/base_kmp
//https://github.com/lsteamer/Chirp.git (Module)
//https://github.com/jonathanwdev/chirp.git
//https://github.com/lsteamer/NutriSport.git (Module ImagePicker)
//https://github.com/Vprogrammers/a11yOtp (OTP Animation)
//https://github.com/pitampoudel/komposeauth.git (State - Event Profile Edit, Google & Apple Login, OTP Good, File Picker Single/Multiple, JWTTOKEN, Multiple Image Download)
//https://github.com/vincenzosarnataro/UsersKMP.git (Koin Annotations, KTOR with K-RETROFIT, KTOR custom header, Test cases)
//https://github.com/baselinesio/baselines-kmp-free.git
//https://github.com/arifamsar/CMP-CleanArchitecture-Starter

//https://github.com/stevdza-san/CurrencyApp
//CineTracker-KMP-main (github gustavopeq) (Detail page image scroll animation, Dynamic Tab)
//https://build38.com/blog/technology/kotlin-multiplatform-efficient-mobile-app-development/
//https://proandroiddev.com/exploring-kotlin-multiplatform-a-visual-guide-734b85132d68
//https://excalidraw.com/
//https://github.com/vinceglb/FileKit //Image Picker
//https://github.com/Seboot2002/compose-multiplatform-book-app
//https://github.com/farimarwat/Loom-Video-Player-CMP
//https://github.com/YangDai2003/Kori
//https://github.com/PalankiBharat/HeroIo
//https://github.com/farimarwat/Internet-Speed-Test
//https://github.com/Irfanulhaq1991/kmmlogin (ConstraintLayout Login)
//https://github.com/robert-muriithi/movies-catalogue-KMM (Horizontal Pagination)
//https://github.com/mejdi14/KMP-Liquid-Search
//https://github.com/epicarchitect/breakbadhabits (AppBar Scroll Text Hide/Show Animation, EpicCalendarPager, LazyColum Multiple Timer, Date Picker and Time Picker Dialog, Hide/Show TextField Animation)
//https://github.com/exyte/ComposeMultiplatformDribbbleAudio (LazyColum Click Actions (Favourite, Delete) Animation)
//https://klibs.io/project/MohammedAlaaMorsi/Color-Picker-KMP
//https://github.com/percy-g2/kmp_utxo (Cryptocurrency Tracker, Desktop, Web and Mobile - Live Update, Network Connection, Navigation Bar Best, Custom Scroll Bar)
//https://github.com/kagg886/Pixiv-MultiPlatform (Favourite With Progressbar)
//https://github.com/karthyks/Ganttastic
//https://github.com/Ehsan-Ullah-Tariq/CMP_KMP_Boilerplate (invoke, last to first back navigation)
//https://github.com/NatashaIlievska/Pokemon-CMP (voyger using parameter pass koin)
//https://github.com/niltsiar/kotlin_multiplatform_pokedex.git (koin, koin named, pagination without library, internal class, Arrow)
//https://github.com/yet300/AdaptiveMultiPanelNavigation.git (Decompose)
//https://github.com/SimonSchubert/Kai
//https://github.com/the-best-is-best/KDownloadFiles (Download Files)
//https://github.com/naikdp7/KMPNewsApp
//https://github.com/mhmmdvg/playit-kmp (spotify api call)
//https://github.com/Coding-Meet/TextToSpeech-CMP
//https://github.com/kabindra-shrestha/Clean-Architecture-Kotlin-Compose-Multiplatform (In-app Update)
//https://github.com/hkr-mobile-dev-team-1/kotlin-multiplatform-app (supabase, voyager, kizitonwoseCalendarVersion)
//https://github.com/mohammadjoumani/validation_jetpack_compose_clean_architecture
//https://github.com/HarukeyUA/ComposeMagneticSwipeToDismissDemo.git (Swipe To Delete Animation Good)
//https://github.com/OneXeor/KDownloader
//https://github.com/be1ski/vibits.git (Calendar, Quarters)
//https://github.com/alexzhirkevich/qrose (Styled QR & Barcode generation)
//https://github.com/Chouten-App/ChoutenKMP.git (BottomBar UI Good)
//https://github.com/Alidon256/Hau.git (WhatsApp UI)
//https://github.com/Nubet/studymood-app-public.git (Calendar, Onboarding AnimatedContent with Button Animation)
//https://github.com/KAbaidoo/Hymnal-CMP.git ()
//https://github.com/huarangmeng/latex (Math Syntax library)
//https://github.com/mamon-aburawi/Table-KMP (Table)
//https://github.com/chrisjenx?tab=repositories (Create PDF)
//https://github.com/mofeejegi/compose-alert-banner
//https://github.com/Haartag/CuteCard
//https://github.com/YuKongA/Updater-KMP.git (Round CheckBox, ToolTip)
//https://github.com/stoyan-vuchev/squircle-shape.git (Good Menu List (Side Menu Left Side, Hide/Show))
//https://github.com/dsqrwym/HomeChat
//https://github.com/stoyan-vuchev/magic-message
//https://github.com/MarwanMohamed32/Mood-Notes-App
//https://github.com/fadibouteraa/formk-kmp (Validation)
//https://github.com/climbeyond/beyondcalendar (Calendar)
//https://github.com/mohammadestk/iPulse.git (MQTT)
//https://github.com/AzizaHelmy/PizzaTask
//https://github.com/makzimi/SwipingCards
//https://github.com/makzimi/scroll-effect-lazy
//https://github.com/RaulETSINF/LiquidPagerCMP (onboarding liquid)
//https://github.com/hosseinkarami-dev/penny-kmp (Expenses, Share CSV)
//https://github.com/DimiVlachos/compose-lab (Navbar, Profile gallery, Fogged mirror, Page-turn book (page curl), Pop-up book, paper plan, Pull cord, Magnet filter, Fishing refresh, )
//https://github.com/Ma7moud3ly/quran-planet
//https://github.com/JALAL591/compose-bridge
//https://github.com/btripp/openflight-mobile-kmp


//https://medium.com/@richardebboh/ui-and-the-view-system-androids-view-system-297d37ae6aa7
//https://medium.com/@Tweeel/how-to-prevent-screenshots-in-kotlin-multiplatform-kmp-for-android-and-ios-280217de148c
//https://medium.com/@Tweeel/how-we-implemented-a-network-connectivity-observer-in-kotlin-multiplatform-kmp-41fee627c686
//https://medium.com/@Tweeel/fix-google-sign-in-failing-in-old-android-devices-in-kotlin-multiplatform-kmp-18ea424a612c

//METRO DI
//https://github.com/smithjustinn84-netizen/waterBuddy.git (Test Case kotest, Metro DI)

//1_10_0
//https://github.com/Rohit-554/Compose_1_10_0.git

//CLEAN ARCHITECTURE
//https://www.davthecoder.com/blog/the-unified-codebase-a-comprehensive-blueprint-for-kotlin-multiplatform-clean-architecture

//KSerializer
//https://github.com/Luwaphemi/DataTruth.git (Datetime)
//https://github.com/mikaere66/DiscoverMultiplatform
//https://github.com/xephosbot/AnilibriaRefresh

//Best Default Access
//https://imagepickerkmp.dev (Camera/Gallery)
//KVerify (Validation) //https://tessoir.medium.com/stop-validating-like-this-theres-a-cleaner-way-6c9007b7f484

//Best Code
//https://github.com/EEseka/Lynk.git (Google Map (Marker Animation), snapshotFlow pagination)
//https://github.com/Troves-eCommerce/Troves.git (Shopify, Connectivity Observer, Paymob Payment, Google Login, E-commerce)
//https://github.com/sunildhiman90/Aegis.git (Change Language, Change Theme, operator fun, Application Setting Manage)
//https://github.com/developerBBee/AssemblePC_KMP.git (Navigation good, KSerializer (Enum, Price operator overloading, TimeZone))
//https://github.com/vahitkeskin/Equatix.git (Dynamic GridView, Dialog left to right and right to left animation, Segment animation, Select item next position automatically move position, Game, Splash Animation, Notification (all platform))
//https://github.com/EEseka/ShelfLife (safeFirebaseAuthCall, platformSafeCall KTOR, Notification, PickedImage)
//https://github.com/mitulbharatchandra/WorldWideMoto (Firebase sign in (google / apple platform code), google Map)
//https://github.com/romreviewer/NoteItUP.git (BiometricType, Notification, CrashAnalytic Good, Location, Encryption, koin context, Swift LAContext())
//https://github.com/RevenueCat/compose-paywall-animations.git (Purchase Screen Animation)
//https://github.com/romancanoniero/firebase-kmp-sdk
//https://github.com/Uwi0/Yuko-App.git (Android/iOS Code)
//https://github.com/Uwi0/Oakane
//https://github.com/H-AbdulMateen/Chirp.git (Chat App, icerock Permission, Firebase Notification, safeCall Platform Base, Backend Kotlin)
//https://github.com/H-AbdulMateen/chirp-api
//https://github.com/akexorcist/workstation-diagram
//https://github.com/MustafaKoceerr/JustRelax.git (File Download With ProgressBar, Audio Player, semaphore)
//https://github.com/Quartel-Enterprise/bible-planner-mobile-client.git (Factory koin, Planner 7 days, Theme change)
//https://github.com/TinaT2/TimerzeerKMP.git (Best Theme change color, Font change, Background change)
//https://github.com/droidwithme/product-catalog.git (Best API Calling, Multiple Module, Pagination, Test Cases, Search/Filter with Combine Good)
//https://github.com/logomount/galaxy-navigation.git (navDeepLink navigation / deep link navigation)
//https://github.com/zakrodionov/km-shop (Best Code)
//https://github.com/JetBrains/kotlinconf-app (Tab to Scroll)
//https://github.com/Jeetin9410/Anand_Mart_KMM_App
//https://github.com/thejohnsondev/PassGuard-Multiplatform (Localization, CSVRead, Firebase, FileManager, Password Length Animation)
//https://github.com/mahdizareeii/ComposeMultiplatform (Google Map)
//https://github.com/software-mansion/kmp-maps (Google map)
//https://github.com/YoussefmSaber/maps-for-cmp.git (Map maplibre (iosMain code))
//https://github.com/mahdizareeii/Mini-Uber.git (Google Map - (swift code))
//https://github.com/EmmanuelDav/Smart-Run.git (Google Map Tracking System (Swift code / Android code))
//https://github.com/codernewbie04/Mapbox-CMP (Mapbox)
//https://github.com/rachel-ylcs/ylcs-kmp (Custom Pagination, Theme, Custom Calendor, SelectionContainer 1.8.0 (Copy Text))
//https://github.com/pjq/ChatCat
//https://github.com/theGBguy/CMPFTSRoom.git (Room Database)
//https://github.com/vlasquez/TechnicalTest_KMP (Exloplayer, KTOR Mock API Call)
//https://github.com/haidrrrry/AnimatedClockJetpacl (Clock)
//https://github.com/Kevn-Block/JuliaSets-KMP (Zoom Image Rectangle)
//https://github.com/fede-debe/NutriSportDemo (PayPal)
//https://github.com/narendraanjana09/CMPRazorpayDemo.git (RazorPay)
//https://github.com/qburst/stripe-kmm (stripe)
//https://github.com/kttipay/KPayment (Good Pay and Apple Pay - All platform)
//https://github.com/Troves-eCommerce/Troves.git (Shopify, Connectivity Observer, Paymob Payment, Google Login, Eccommerce)
//https://github.com/sunnat629/CoffeeMachine
//https://github.com/Junaed29/VendingMachineMultiplatform (Storage - Android, iOS, WASM, Desktop)
//https://github.com/delacrixmorgan/kingscup-kmp (Language Change)
//https://github.com/thejohnsondev/PassGuard-Multiplatform
//https://github.com/Cairo-Squad/EvolveFit (Change Theme, WeightPicker, DatePicker, RefreshToken, PDF Create)
//https://github.com/kotlang-dev/FreelancerFinance.git (PDF Create, Room DB Join Query)
//https://github.com/delacrixmorgan/twilight-kmp (WeightPicker)
//https://github.com/mheerwaarden/CMPEventDemo.git
//https://github.com/mahdizareeii/Mini-Uber (Viper architecture)
//https://github.com/taetae98coding/Diary-App (Koin annotation best)
//https://github.com/love-yuri/journal-kmp.git (Swipe to Refresh / Pull To Refresh Custom UI using pointerInput, LazyColumn Animation)
//https://github.com/gleb-skobinsky/ThemeAnimator.git
//https://github.com/Ali0092/Taskaroo_Mulitplatform.git (DateTimePicker Platform Specific)
//https://github.com/stevdza-san/Swipeable-KMP
//https://github.com/Alyasa62/drawing-thoughts-KMP (Drawing Application)
//https://github.com/LexiLabs-App/basic-ads/tree/v1.1.0-beta03 (Add No need Context)
//https://github.com/Danny3042/Lifely.git (Health App)
//https://github.com/mohaberabi/konsent (Permission)
//https://github.com/Alidon256/Hau (Chat UI)
//https://github.com/dalafiarisamuel/Unsplash-KMP.git (Ktorfit, KtorDsl, Platform (Mobile/Desktop) Download Image, ExplicitBackingFields)
//https://github.com/Cairo-Squad/EvolveFit.git (workout app, weight vertical picker)
//https://github.com/MeltingMadness/Thrym.git (Fitness, LazyRow Animation, Chart, Liquid Glass Good, CompositionLocalProvider)
//https://github.com/lopezjavier/AppFitness (Fitness)
//https://github.com/pddhkt/workout-app.git (workout app, BottomBar Hide/Show)
//https://github.com/amary21/AmaryPay.git (Backend Firebase Notification, Firebase Notification (iOS/Android )  Payment Screen Good)
//https://github.com/DeFerence3/form-doctor (Validation)
//https://github.com/dalafiarisamuel/Unsplash-KMP.git (KoinComponent, Koin using Get argument, field kotlin)
//https://github.com/atekik/sahaf-cmp (orbit-mvi library)
//https://github.com/aldefy/Lumen (overlay scrim)
//https://github.com/ioannisa/KSafe
//https://github.com/thementalgoose/kmp-flashback.git (Drawer Good, Firebase Platform Specific Code (Analytics, RemoteConfig), XML KTOR API Call, Desktop App good)
//https://github.com/Montfel/FIPE.git (internal keyword use Clean archi)
//https://github.com/kalist28/bubbles-compose ()
//https://github.com/idineszam2374/kmp-luckin-coffee-clone.git(Bounce animation Shopping Item BottomBar)
//https://github.com/arifamsar/Subot.git (liquid, Localization, Navigation3)
//https://github.com/Grigoriym/Mukk.git (Desktop Folder Tree Panel, Music App)
//https://github.com/Knthcame/MyHealthKMP.git (Heart Rate Flow, Skie, Kotlin 2.3.0)
//https://github.com/AhmedOmara14/Kmp-Architecture (TextField Default Value Store, TimeLine ConstraintLayout)
//https://github.com/giovanileitevitor/StockIN
//https://github.com/dalafiarisamuel/NfcReaderKMP
//https://github.com/ShanuDevCodes/KITO.git (Best Navigation3 Animation, Find Dynamic Position, Bottom Hide/Show Animation, hazeEffect, Calendar, Auto Scroll Index)
//https://github.com/mnebot-6/Riptide.git (Calendor)
//https://github.com/yoesuv/Info-Malang-Batu-KMP (Google Map, Marker)
//https://github.com/mamon-aburawi/Printer-KMP
//https://github.com/mamon-aburawi/ComposeTableKMP
//https://github.com/theadityatiwari/RulerKit
//https://github.com/KodeinKoders/CuP.git (Slider, highlight code, run app command - ./gradlew :Demo:hotRunJvm)
//https://github.com/LinkoraApp/Linkora.git (Onboarding good)
//https://github.com/mofeejegi/compose-alert-banner/tree/nightly (Toast Multiple)
//https://github.com/toby-gamez/TKOlympApp.git (sh1 certificate, mutex using cache manage, lazy define)
//https://github.com/lerchenflo/SchneaggchatV3.git (Chat App, snapshotFlow combine sorting)
//https://github.com/karrad1201/tickets-client.git (BottomBar Animation, QR Code - Scanner)
//https://github.com/yuroyami/DuelistLP.git (Marquee Image)
//https://github.com/mahmoud947/KPDF
//https://github.com/darriousliu/PiPixiv
//https://github.com/AlvaroQ/adivina-raza.git (Quiz Firebase, Shake Animation)
//https://github.com/NadeemIqbal/krumb (Toast Message with underline progressbar)
//https://medium.com/@Vetal_3534/local-notifications-in-compose-multiplatform-a-complete-guide-for-android-and-ios-92a2d87374fb
//https://github.com/CyroPCJr/Chirp-App
//https://github.com/milhauscz/RickAndMorty-Android-Playground
//https://github.com/Hussienfahmy/GPA_Manager.git (SwapItem, Firebase Login, LazyColumn Swap Item Animation (MoveUp - MoveDown Item, Firebase Notification (Good), Platform Specific Bottom Navigation)
//https://github.com/PiotrPrus/KBlobs.git

//GAME
//https://github.com/AchalVishnoi/SpadeBreak-Live-Front-end-.git (Card Game)
//https://github.com/AchalVishnoi/SpadeBreakLive_Backend
//https://github.com/Nacchofer31/RandomBoxd.git
//https://github.com/smithjustinn84-netizen/Memory-Match.git (Card Game)
//https://github.com/oguzhandurmaz/lexiword-kmp.git (Orbit Library MVP)

//SCANNER
//https://github.com/MobileByteLabs/scankit-kmp
//https://github.com/ismai117/KScan
//https://github.com/RufenKhokhar/MLKit-KMP (Barcode)

//PLATFORM SPECIFIC
//https://blog.jetbrains.com/kotlin/2026/09/a-more-reliable-compilation-scheme-for-kotlin-multiplatform-modules
//https://github.com/mhmmdvg/playit-kmp (spotify api call)
//https://github.com/mhmmdvg/service-apps-be (KTOR BACKEND)
//https://github.com/tarifchakder/NetworkObserver (internet connection check)
//https://github.com/iosifvilcea/baseapp (internet connection check flow, refresh token)
//https://github.com/YoussefmSaber/maps-for-cmp (Google Map)

//Desktop
//https://github.com/YRSB/RePoi.git (Scrollbar, pagination without pagination library)
//https://github.com/deeery/StuMate-Desktop (Best Weekly calendor details Display)
//https://github.com/Shahidzbi4213/WallpaperApp-Cmp.git (Good Number pagination Desktop with AnimatedContent, Download, Set Wallpaper)
//https://github.com/jonathanwdev/petappKMP
//https://github.com/xephosbot/AnilibriaRefresh.git (Good UI)
//https://github.com/ADRIELCELSOROSALES/KanvaTask.git
//https://github.com/shub39/RpiAttendance.git (Python, Attendance App)
//https://github.com/KyNarec/SubSched.git (Desktop Full Screen Option, BoxWithConstraints, Bottom Bar Visible Multiple Navigation)
//https://github.com/Ma7moud3ly/quran-multiplatform.git (koin annotation using font, Desktop, Web, Tablet, Mobile App, Firebase)
//https://github.com/RznNike/DemoKMP.git (Good API Calling (webSocketManager, ktorfit, Common UseCase, Common ViewModel))
//https://github.com/VinhCterVN/compose-multiplatform-ChatApp.git (Chat App)
//https://github.com/VinhCterVN/cmp-transfer-cloud.git (File Transfer Socket & File Encrypt)
//https://github.com/KodeinKoders/CuP.git (Slider, highlight code, run app command - ./gradlew :Demo:hotRunJvm )
//https://github.com/terrakok/FuzzyKot (Search highlight Text)
//https://github.com/UmairOye/ComposeCurvedBottomNavigationView.git
//https://github.com/UmairOye/fluid_splash_kotlin_multiplatform (Introduction Screen Animation)
//https://github.com/Quantum3600/Optimite.git (Memeory Management)
//https://github.com/Sherif-Moheep/SharePointVideoDownloader (Download)
//https://github.com/senseFy/quota-dog.git
//https://github.com/imansprn/gost-desktop.git (Material3)
//https://github.com/j-roskopf/Phoebe (Music All Platform)
//https://github.com/farizakbar11/TestSpire-Desktop-Test-Case-Management.git (Photo Upload & Zoom)
//https://github.com/FabianZettl/NeiroDesktop.git (Music App, Music Line Text Change)
//https://github.com/Yoke0/Gainful (Calendar, BottomBar Background Spring Animation, Tab Background Spring Animation)
//https://github.com/imkao0/MiraSalon-KMP.git (Desktop, Mobile, File Upload, Calendor, Chat)

//Database
//https://github.com/Egi10/sainggik (Sqllite Di Best)

//Test cases
//https://github.com/matthieucoisne/YelpExplorer-CMP
//https://github.com/PiDyGB/CMPMyNASADailyPics (Local Database, Android, iOS, Desktop)
//https://github.com/vincenzosarnataro/UsersKMP.git (Custom Header, Retrofit, Koin Annotations, Test cases)

//Best API Calling
//https://github.com/rubenquadros/TimeToWish-App (KTOR, Common UseCase, Common Flow, Google login, Android Context Pass)
//https://github.com/arrazyfathan/tudu-mobile (Validation UseCase, Module platform specific, DataStore)
//https://github.com/MrWolfeDev/News-app-in-KMP (Desktop and Mobile Theme default find)
//https://github.com/Cj-Rodriguez101/Vicinity-KMP (Ticket Master Event, Key: koa9PmuEh7GEOJzChcGeolAqMAhr69kf, Google Map (Mobile, Desktop), Calendar)

//My Best App
//https://github.com/hyperiontechllc/fluid-bottom-bar (Bottom NavigationBar)
//https://github.com/ali-roozbahani/TrailMetrics.git (Map Best Tracking)
//https://github.com/iqbalwork/ramadhan-camp-kmp.git
//https://github.com/MohamedRejeb/compose-dnd.git (Drag & Drop Good, Web)
//https://github.com/HachimiWorld/hachimi-world-client.git (Music (exoplayer, SubcomposeLayout), WASM, Mobile Platform)
//https://github.com/pmhudak13/my-car-companion-kmp (voyger using koin, SUPABASE)
//https://github.com/Helmy2/Mudawama
//https://github.com/KuLiPai/JNU-EXAM-Client.git (BottomBar Liquid)
//https://github.com/Luhangh/mvi-app
//https://github.com/T8RIN/ImageToolbox
//https://github.com/KempLi/Eyepetizer
//https://github.com/ShreyashKore/wonderous_compose
//https://github.com/Grippo-app/grippo-mobile
//https://grippo-app.github.io/grippo-site (snapshotFlow pagination, Validation Good, Koin Module, Token Manage Mutex, Custom Theme, Custom Compose, KTOR (runcatching, withContext(Dispatchers.IO)), Context App Level, Google/Apple Login, Segment, Wheel Multiple Compose Weight Picker, Body Part Select) (code - https://github.com/Grippo-app/grippo-mobile.git)
//https://github.com/milkandvodka/KITO (Best Navigation3 Animation, Find Dynamic Position, Bottom Hide/Show Animation, hazeEffect, Calendar, Auto Scroll Index)
//https://github.com/EfeObus/VwaTek-Apply.git (FrontEnd - BackEnd - App)
//https://github.com/j-roskopf/Phoebe (Music App All Platform)
//https://github.com/odaridavid/Smoovie-KMP (LazyColumn Animation, LazyColumn LazyListScope)

//China Code
//https://github.com/Team-Bigong/oguri-kotlin-multiplatform.git (Metro DI, OnBoarding AnimatedContext good, calendar, apple & google login, Spring backed, react admin panel)
//https://github.com/Joker-x-dev/CoolMallKotlin (SharedTransitionScope, Custom Pull Down Refresh, Scroll Category Selection, Chat UI Animation, Gird/List Animation, RattingBar)

//Share Code
//https://github.com/KTSnippetByShubham/KmpNativeDatePicker (Date Picker)
//https://imagepickerkmp.dev (Image/ Gallery Picker and Compress)
//https://github.com/ShahzaibAli02/InstaSaver-Kmp.githttps://github.com/ShahzaibAli02/InstaSaver-Kmp.git (Background Download, purchases KMP, Firebase Token)
//https://github.com/leonardobehnck/location-tracker.git (Location Track)
//https://github.com/ArcaDone/Scanpose (Barcode Scanner)
//https://github.com/malindu21/bookmanager-cmp
//https://github.com/filipebatista/KDragonBall (Shared UI, lint style check, ViewModelProvider.Factory)
//https://github.com/brewkits/kmpworkmanager
//https://github.com/mohaberabi/konsent (Permission Handle)
//https://github.com/mohaberabi/kueue (Thermal printer)
//https://github.com/KevinBlock-GC/kmp-firebase-auth
//https://github.com/nyuksoonvong3432/kmp-todoist-app
//https://github.com/matt1007k/Cine-CMP (Native Button and Map)
//https://github.com/the-best-is-best/KDownloadFiles
//https://github.com/surajpsp/WhetherKKotlin
//https://github.com/aslibyr/MoviesKMM (Good code)
//https://github.com/prashant17d97/biometric-authentication-
//https://github.com/InonELGABSI/TopOut (Share UI, Test Cases, Notification iOS Kotlin, Location Get)
//https://github.com/ronyaburaihan/Weatherly-KMP.git (Permission)
//https://github.com/Robert-P-M/Stormbreaker_KMP.git (Weather best app, Notification, Permission and Scroll Auto Selection Slider Tab, Click Tab Scroll)
//https://github.com/tridang-regentech/blockchain-kit-sdk
//https://github.com/seabat/ramen-note (Camera/Gallery Permission Best, touchlab.skie, KSP Kotlin, Image Save, Fetch, Rename and Delete, Room Database Latest)
//https://github.com/wshtaits/GetLike.git (AppBar Scroll Hide/Show Animation, ConstraintLayout, Firebase, Firebase Message, State Flow Connection Check, Image Picker, Contacts Get)
//https://github.com/MarketTwits/Sportsouce.git (Multiple Base URL)
//https://github.com/dalafiarisamuel/ContactPickerKMP (Contact Picker)
//https://github.com/M4kkenzEe/PockerWardrobe.git (Permission Manager Camera & Gallery)
//https://github.com/RandhirGupta/PaperwalaApp.git (News App Animation Good - Not Check)
//https://github.com/jordanrafalm/quiz-pszczelarski-kmp.git (Quiz App)
//https://github.com/GyuminLee/Bimil-KMP.git (Admob, Biometric, File Picker, Desktop App)
//https://github.com/thementalgoose/android-hour-glass.git
//https://github.com/OlegDaniv/print-business-kmp.git (Backend postgres, Web, Desktop App, File Choose, Save File, Code delete github)
//https://github.com/Ma7moud3ly/quran-multiplatform.git (koin annotation using font, Desktop, Web, Tablet, Mobile App, Firebase)
//https://github.com/com3run/lifeplanner.git (Firebase Notification)
//https://github.com/irwancannadys/WebSocketComposeMultiplatform.git (Socket Price Update with Background Color Change)
//https://github.com/skymansandy/kurlclient (JsonViewer Desktop App)
//https://github.com/skymansandy?tab=repositories (All platform)
//https://github.com/ResulSilay/Sentinel.git (Bottom Navigation Bar Good, Scroll to Hide Header)
//https://github.com/jasi381/FirebaseAuthKMP (Video Calling Agora, Firebase)
//https://github.com/akobartek/SMWolczyn.git (QR Code Scanner)
//https://github.com/gcaguilar/biciradar.git (BindingContainer, ContributesTo,  ContributesBinding, GraphExtension, Google Map)
//https://github.com/rroohit/CmpImageCropView
//https://github.com/rexiox/sentinel
//https://github.com/synapseSRC/synapseApp (supabase, Security, Chat App, File Upload)
//https://alims-repo.github.io/FlowTab-CMP (BottomBar Navigation)
//https://github.com/Dev-Aditya-More/FaceNox (image editor with face detection, filters, and background removal)
//https://github.com/amitshilo11/menudeldia.git (Metro, Google Map)
//https://github.com/azer-ben-zineb/habit-trucker (Notification, DateFormate both platform)

//POS
//https://github.com/ShaoWenbinSaleh/ComposeXPOS.git

//Test Cases
//https://github.com/djkovrik/Tackle
//https://github.com/enmanuel52/CornTime.git

//Library
//https://github.com/big-jared/kmpdf

//TODO APP
//https://github.com/sbusceti/Krono
//https://github.com/SameerShelarr/toodo-backend.git (Spring + KMP)

//Cross UI
//https://github.com/adamglin0/compose-continuous-rounded-corner-shape

//https://github.com/farimarwat/KrossUi
//https://github.com/bizilabs/halo
//https://github.com/RavenZIP/Composia
//https://github.com/flyfishxu/vetra-ui.git (TextField Bottom Line Animation, Pull To Refresh Custom UI, Toast Multiple / SnackBar Multiple)

//KMP
//https://github.com/nfn8y/JustNotes

//WASM
//https://web.clashally.com (https://www.linkedin.com/in/moinkhan-in)
//https://github.com/EfeObus/VwaTek-Apply.git (FrontEnd - BackEnd - App)
//https://github.com/youndie/haul.git (Ecommerce/Shopping Web - Backend)
//https://github.com/zeeshanali-k/DevScionWeb
//https://github.com/furkanozendev/furkanozendev-portfolio.git
//https://github.com/yjyoon-dev/yjyoon.dev
//https://github.com/Egi10/personal
//https://github.com/smoralb/Myresume.git
//https://github.com/smoralb/kotlin-multiplatform-full-statck
//https://github.com/jasonctoms/JasonToms?tab=readme-ov-file
//https://hamdy-abd-el-fattah.netlify.app
//https://github.com/iamrv-sachan/PortFolio.git (Code Github Present)
//https://github.com/alexdevep7/portfolio-web.git
//https://github.com/tarifchakder/portfolio
//https://github.com/nana-adrien/Portfolio.git
//https://github.com/johnsonjsyuen/kotlin-wasm-todo.git (TODO Terminal)
//https://github.com/nmstarry/QYTQWASM.git (Weather App, Drag Drop Good - Website link - https://qytq.nmstarry.qzz.io/#HOME)
//https://github.com/Helmy2/Helmy2.github.io.git
//https://github.com/Hey-Deepak/heydeepak.dev (Start Loading Bar)
//https://github.com/matsumo0922/matsumo-me-KMP (Good)
//https://github.com/SahilKarwasra/KMP-Portfolio.git (Good Scroll to line animation)
//https://github.com/rajdeepvaghela/AboutMe.git
//https://github.com/iammohdzaki/OneDev.git
//https://github.com/darkpandawarrior/cv-siddharth-kmp.git (Good)
//https://github.com/pmhudak13/my-car-companion-kmp.git (File Picker, Geo Location)

//TABLE VIEW
//https://github.com/stephenWanjala/KomposeTable
//https://github.com/White-Wind-LLC/table

//CHART
//https://github.com/swithun-liu/cmp-mermaid.git (All Types of Chart)
//https://www.patrykandpatrick.com/vico/guide/stable/getting-started
//https://github.com/patrykandpatrick/vico
//https://github.com/filin2hat/BikeCalcMP
//https://github.com/hi-manshu/charty
//https://github.com/TheChance101/AAY-chart
//https://github.com/KoalaPlot/koalaplot-core
//https://github.com/hossein-no1/compose-multiplatform-linechart
//https://github.com/rootachieve/Koraph
//https://github.com/kotalsumit/compose-super-charts
//https://github.com/AndroidPoet/Drafter
//https://github.com/HDCharts/charts
//https://github.com/youndie/mani.git (Desktop and Web)

//SHOPPING APP
//https://github.com/sajibsaj66/Ndulaz

//BLE
//https://github.com/SebaUbuntu/OpenOBD.git
//https://github.com/D10NGYANG/DLBluetoothUtil
//https://github.com/D10NGYANG/DLSerialPortUtil
//https://github.com/Rohitdacoder/KMP-BLE-Scanner.git
//https://github.com/gary-quinn/kmp-ble
//https://github.com/gary-quinn/kmp-nfc
//https://github.com/gary-quinn/kmp-uwb

//WEAR OS
//https://github.com/ResulSilay/otix.git

//Heart Information
//https://github.com/AnatoliiChub/HRAM.git (Koin Annotations @KoinApplication, @Module, @Configuration, @Factory, @KoinViewModel, Koin Validation, Bluetooth, Heart information)

//SpringBoot
//https://github.com/gowthamkofficial/blogs-fullstack (File Upload)
//https://github.com/Sviat98/TennisScoreKeeperBackend (KTOR Websocket Backend + Mobile Application KMM)
//https://github.com/Narsuf/KtStonks (Backend koin + Mobile App)

//KMP React
//https://github.com/Shieru292/kmp-todo-app (tailwind) (db - Database.connect("jdbc:sqlite:data.db", driver = "org.sqlite.JDBC"))

//NOTIFICATION
//https://github.com/Tweener/alarmee

//Purchase App
//https://www.kmpship.app
//https://www.linkedin.com/in/rodrigo-martin-dominguez-463b5a33 (Animation)

//revenuecat
//https://github.com/CagriOrcan/ChristmasGrapes.git

//SwiftUI
//https://www.vadimbulavin.com/swift-combine-framework-tutorial-getting-started/
//https://canopas.com/swift-11-useful-combine-operators-you-need-to-know-499cfdce0cd5

//BackEnd
//https://github.com/OlegDaniv/print-business-kmp
//https://github.com/ProjektMagma/MagmaQuiz.git (quiz backend postgresql + Application)
//https://github.com/pollyannaanalytics/JustWoo-KMP (postgresql + Application)

//POSTMAN
//https://signoz.io/

//Shared Code (React.js implement)
//https://github.com/braiso-22/TerraCambio

//Video
//https://www.youtube.com/watch?v=M9c_CacA748

//DataGridView/Table
//https://github.com/unchil/ComposeDataGrid.git

//WEAR
//https://github.com/GeoSId/weatherwatch-wear-sync.git

//WEATHER APP 
//https://github.com/alexiyous/Weatherio-KMP.git

//https://itnext.io/weak-references-in-kotlin-multiplatform-7403ddb5acb7

/*
fun setRangingCallback(callback: (peerId: String, distance: Double) -> Unit)
fun setErrorCallback(callback: (error: String) -> Unit)

 sourceSets {
        all{
            languageSettings {
                compilerOptions{
                    freeCompilerArgs.add("-Xexpect-actual-classes")
                }
            }
        }
 }
*/

//https://jermeyyy.github.io/quo-vadis/
//https://www.strv.com/blog/kotlin-multiplatform-in-production-what-worked-what-didn-t

//https://medium.com/@sehajkahlon437/kmp-basics-complete-guide-with-real-counter-app-534b414457fb
//https://medium.com/proandroiddev/how-does-the-same-kotlin-code-run-on-both-android-and-ios-the-compiler-magic-behind-kmp-b4450f87f7a8

//Plugin
//https://github.com/matheus-corregiari/arch-lumber
//https://github.com/alexiyous/kmp-vector-asset-plugin
//https://plugins.jetbrains.com/plugin/28221-codemetrics-with-kotlin
//https://plugins.jetbrains.com/plugin/31310-kmp-resources-unfold

//BLOG
//https://kmpweekly.com/ (Weekly Update)

//Swift UI
//https://github.com/space-code/validator (Validation)

//https://forums.swift.org/t/integrating-kotlin-native-into-swift-build/84207

//opensooq database
//PostHog Analytics (https://medium.com/@Tweeel/how-to-integrate-posthog-analytics-in-kotlin-multiplatform-kmp-fe2c8f8d3043)
//https://github.com/divkit/divkit (UI Kit)
//https://github.com/flyfishxu/vetra-ui (UI Kit)
//https://github.com/kalist28/bubbles-compose.git (UI Kit)
//https://github.com/uangkuid/OrataDesign-Web.git (UI Kit)

//https://kmpweekly.com
//https://levelup.gitconnected.com/kmp-threading-on-ios-how-to-keep-ui-state-on-main-63da22576fe2
//https://www.droidunplugged.com/2026/01/cracking-android-sde2sde3-interviews-in_7.html
//https://medium.com/@elna_dev/achieving-single-source-of-truth-for-app-versioning-in-kotlin-multiplatform-893445f68bf6 (Auto Version Update)
//https://medium.com/@santimattius/saying-goodbye-to-cocoapods-survival-guide-for-migrating-to-swiftpm-in-kotlin-multiplatform-f46bda902eb0
//https://medium.com/@cristurean.marius.ovidiu/bridging-the-gap-integrating-kmp-into-pure-native-apps-without-breaking-the-workflow-2f0b17d25008
//https://medium.com/compose-d/constraints-in-jetpack-compose-8cc3b84ec597
//https://blog.csdn.net/weixin_37042458/article/details/148681758 (Basic KMM)
//https://dev.to/johalputt/internals-how-kotlin-20-compiles-to-javascript-20-with-kotlinjs-and-webpack-590-14e6

//https://terrakok.github.io/Compose-Multiplatform-Wizard

//https://kpkg.dev (Library KMM List)
//https://excalidraw.com

//./gradlew :shared:assemble
//open -a "Android Studio" ./MyApp
//KMM for Shared business logic android & iOS.
//CMP for KMM + CMP + UI
//KMP that is where you enable you extend your KMM logic to the other platforms as well that is desktop, that might be your JS ecosystem right So those sort of things bundled together.

//KMP LEARNING
//https://github.com/prahaladsharma/KMP-Experts

//Book Demo
//https://www.safetrax.in/employee-transport-management-system